/**
 * Settings → Storage → Chat storage (#758).
 *
 * One section added to the existing page. Everything else on it — By type,
 * Largest folders, Reclaimable, Duplicates — is untouched, including the
 * five-site quota SQL none of this goes near.
 *
 * ── Why a member's track length is their LIMIT ───────────────────────────
 *
 * Every track is drawn to ONE scale (`PX_PER_BYTE`), so equal pixels mean
 * equal megabytes and the column can be read straight down: a 1 GB member's
 * track really is twice a 500 MB member's. A uniform track would show only
 * percentages, and a member at 92% of 500 MB would look worse than one at
 * 45% of 1 GB while using half as much space — which is the opposite of what
 * an admin hunting for consumption needs to see.
 *
 * ── Sorted by what they USE ──────────────────────────────────────────────
 *
 * Not by percentage and not by limit. The admin is looking for who is filling
 * the drive. The server already returns them in that order; the client does
 * not re-sort, so one answer, one place.
 *
 * ── The two buttons that do nothing yet ──────────────────────────────────
 *
 * `Clean up` and the row menu's `Force clean up` are rendered and inert
 * (Season, 2026-10-15). They need the same refactor as the 12-month
 * retention sweeper — extracting the six-step session delete out of its HTTP
 * route — so they are wired in the stacked PR rather than having that
 * refactor written twice. They are deliberately NOT disabled-looking: Season
 * asked for the layout to be complete. If that PR slips, these ship dead.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useTranslation } from '@papan/i18n'
import { GlassToggle, avatarBg, getInitials, nameFromEmail, useConfirmOptional } from '@papan/core-ui/ui'
// The deep path, not the barrel: a one-value store has no business riding a
// barrel that tests mock wholesale.
import { useChatHomeAvailable } from '@papan/core-ui/ui/chatHomeAvailability'
import { storageApi, type ChatStorageResponse, type MemberChatStorageRow, type ChatStorageSessionRow, type ChatStorageFileRow, type ChatStoragePendingRequest, type ScheduledCleanupRow, type CleanupActivityRow, type MemberCleanupView, type CleanupBooking, type CleanupTake } from '../../client/storage'
import { formatBytes, Section, formatDateWithYear, formatTimestamp } from '../budget/bits'
import { AdjustChatLimitDialog } from './AdjustChatLimitDialog'
import { RoleChatDefaultsDialog } from './RoleChatDefaultsDialog'
import { queueSessionTab } from '@papan/agent/ui'
import { freedByDeleting } from './sharedWithAnotherChat'
import { formatWaitingSince } from './waitingSince'
import { getApiBase, pinnedBatch } from '@papan/api-client'
import { damagedMessageFrom } from '@papan/types'
import { CleanUpChatStorageDialog } from './CleanUpChatStorageDialog'
import { CleanUpMembersDialog } from './CleanUpMembersDialog'
import { activityUnderWay, liveUnderWay, nextFlipAt } from './cleanupVerdict'
import { CleanupActivityDialog, CancelOutcomeLine, type CancelOutcome } from './CleanupActivityDialog'

/** 2 GB is the widest limit the Adjust-limit dialog's PRESETS offer, and it
 *  fills the track. Everything narrower is proportional to it.
 *
 *  The scale therefore stops at 2 GB: the custom MB field takes any whole
 *  number, so a 10 GB member and a 2 GB member draw identical full-width
 *  tracks. "Equal pixels mean equal megabytes" holds below the cap and not
 *  above it (full review 3C, finding 8) — the figure beside the bar is
 *  what stays exact. */
const TRACK_PX = 280
const WIDEST_LIMIT = 2 * 1024 * 1024 * 1024

/** An unlimited member has no track to draw — there is no denominator. They
 *  get the full width in the neutral tone and the figure says so. */
const BAR_OK = 'bg-cyan-500'
const BAR_WARN = 'bg-amber-500'
const BAR_FULL = 'bg-rose-500'
/** UNLIMITED. Filled to the end, but in a tone that is not part of the
 *  ok/warn/full language the other rows speak — a full CYAN bar would
 *  read as "100% of their limit", which is the opposite of having none. */
const BAR_NEUTRAL = 'bg-zinc-400/50'

const INITIAL_ROWS = 5

/**
 * One row of the member list.
 *
 * ── Why the columns are FIXED and the name flexes ───────────────────────
 *
 * Everything after the name has a fixed width, so the figure column and
 * the ⋮ sit at the same x on every row and the column can be read down.
 * Letting the figure size to its content put "520.0 MB of 2.00 GB" and
 * "60.0 MB" at different widths, which pushed each row's ⋮ somewhere else
 * and, at a middle width, pushed the figure out of the card entirely.
 *
 * The track is the one thing that cannot flex: its LENGTH IS THE LIMIT, on
 * one scale across all rows, so a proportional track would destroy the
 * comparison it exists for. It is therefore dropped below `lg` instead —
 * comparing rows is what a wide screen is for, and the figure says the
 * same thing in words at any width.
 */
function StorageRow({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2.5 py-1.5 min-w-0">{children}</div>
}

/** The fixed right-hand columns, shared so the ⋮ cannot drift between the
 *  member rows and the unattributed one. */
const FIGURE_COL = 'w-[150px] shrink-0 text-right text-xs tabular-nums whitespace-nowrap'

/**
 * A live cleanup as the member list needs it.
 *
 * ⚠️ `state` IS NOT DECORATION. `listScheduled` returns `scheduled` AND
 * `running`, deliberately — the badge must not vanish while a worker is
 * carrying a booking out. But only a `scheduled` row can be cancelled
 * (`cancel`'s WHERE says so), and the menu used to offer "Cancel scheduled
 * cleanup" for both: on a `running` row that button could only ever answer
 * 409 "That cleanup is no longer scheduled" about a row the screen itself
 * was showing. The worst case is this branch's own resting run-now, where
 * the conversations are already deleted (round 11, finding 3).
 */
type LiveCleanup = { id: string; runAt: string; state: 'scheduled' | 'running' }

/** A live booking as it stands at `nowMs`: one whose time has come reads
 *  `running` (`liveUnderWay`), so every row reading `state` — its dot, its
 *  label, its menu's Stop vs Cancel — follows the one rule. */
function asOfNow(cu: LiveCleanup | undefined, nowMs: number): LiveCleanup | undefined {
  return cu && cu.state === 'scheduled' && liveUnderWay(cu, nowMs) ? { ...cu, state: 'running' } : cu
}

function MemberRow({ m, onAdjust, onCleanUp, selected, onSelect, scheduled, onCancelScheduled }: {
  m: MemberChatStorageRow
  onAdjust?: (m: MemberChatStorageRow) => void
  /** Absent for a read-only caller, exactly like `onAdjust` — the menu is
   *  withheld as a whole rather than opening onto an action that would 403. */
  onCleanUp?: (m: MemberChatStorageRow) => void
  /** Ticked for the multi-member cleanup. Absent = no picker (a read-only
   *  caller, or the member's own view). */
  selected?: boolean
  onSelect?: (email: string, next: boolean) => void
  /** A booking already exists for this member. The design's rule: cancel
   *  it from their own row before booking another, and the database
   *  refuses a second one regardless. */
  scheduled?: LiveCleanup
  onCancelScheduled?: (id: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const [menuOpen, setMenuOpen] = useState(false)
  const name = nameFromEmail(m.email)

  const unlimited = m.limitBytes === null
  // A limit of ZERO is a hard block, not "no limit" — `parseChatStorageLimit`
  // accepts 0 deliberately, the role dialog warns about it by name, and any
  // role outside `DEFAULT_ROLE_CHAT_STORAGE` resolves to it. `!m.limitBytes`
  // swallowed that case, so a blocked member with 50 MB stored drew an EMPTY
  // CYAN bar beside "50.0 MB of 0 B" — the healthiest-looking row on a page
  // the admin scans to find who is in trouble (full review 3C, finding 3).
  const blocked = m.limitBytes === 0
  const ratio = m.limitBytes === null ? 0 : blocked ? 1 : m.totalBytes / m.limitBytes
  const trackWidth = unlimited
    ? TRACK_PX
    : Math.max(8, Math.round((Math.min(m.limitBytes ?? 0, WIDEST_LIMIT) / WIDEST_LIMIT) * TRACK_PX))
  // Over limit FILLS the track and stops. A bar running past its own end
  // reads as a rendering fault, and the figure beside it already says
  // "512 MB of 500 MB".
  //
  // An UNLIMITED member fills it too, in the neutral tone. Left empty, a
  // member holding 50 GB with no limit drew the emptiest bar on a list
  // whose whole purpose is finding who is filling the drive, while a
  // colleague at 80% of 500 MB drew amber — only the text column told
  // them apart (full review 4C, finding 12). There is no denominator, so
  // the bar says "a lot of bytes, measured against nothing" rather than
  // "nearly none".
  const fillPct = unlimited ? 100 : Math.min(100, Math.round(ratio * 100))
  const barColor = unlimited ? BAR_NEUTRAL : ratio >= 1 ? BAR_FULL : ratio >= 0.8 ? BAR_WARN : BAR_OK

  return (
    <StorageRow>
      {onSelect && (
        // Ticking chooses WHO, and nothing else. A member who already has
        // a booking cannot be ticked: the database refuses a second one,
        // so offering the tick would only produce a skip at the end.
        <input
          type="checkbox"
          checked={!!selected}
          disabled={!!scheduled}
          onChange={(e) => onSelect(m.email, e.target.checked)}
          aria-label={name}
          className="shrink-0"
        />
      )}
      <span
        className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10.5px] font-semibold text-white"
        style={{ background: avatarBg(m.email) }}
      >
        {getInitials(name)}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13.5px] themed-text">{name}</span>
      {/* The row's own states, as dots — see `StatusDot`. An admin scans
          this list for exactly these three things, so they stay; they
          just stop costing the figure its place on a narrow screen. */}
      {ratio >= 1 && <StatusDot tone="danger" label={t('settings.storage.cleanupOverLimitBadge')} />}
      {scheduled && <StatusDot tone="info" active={scheduled.state === 'running'} label={t(scheduled.state === 'running'
        ? 'settings.storage.cleanupRunningBadge'
        : 'settings.storage.cleanupScheduledBadge')} />}
      {/* CUSTOM came off this row in October for taking too much space
          (Season). As a dot it costs 8 pixels, and "why is this person's
          bar a different length" is a real question the list could not
          answer without opening the dialog. */}
      {m.hasOwnLimit && <StatusDot tone="muted" label={t('settings.storage.chatStorageCustomBadge')} />}
      <span className="hidden lg:block shrink-0" style={{ width: TRACK_PX }}>
        <span
          className="block h-[7px] rounded bg-black/20 overflow-hidden"
          style={{ width: trackWidth }}
        >
          <span className={`block h-full ${barColor}`} style={{ width: `${fillPct}%` }} />
        </span>
      </span>
      {/* BLOCKED GETS A WORD, the way UNLIMITED does. `chatStorageOf`
          rendered a blocked member as "<what they hold> of 0 B" — "0 B of
          0 B" for someone holding nothing, "50.0 MB of 0 B" for someone
          holding plenty, and an admin scanning this list for who is in
          trouble has no word for either. (An earlier version of this
          comment said it rendered "0 B of 0 B" flatly, which is only the
          empty case; the comment 35 lines up has it right.) The bar colour
          was fixed for this
          case three rounds earlier and the TEXT was left, so the member's
          own page said "Blocked — no allowance / you cannot attach
          anything in chat" while the admin's list described the same
          person in a vocabulary that has no word for it (full review 43C,
          B6). Short form, because this column is dense; the member's own
          view keeps the full sentence. */}
      <span className={`${FIGURE_COL} themed-text-secondary`}>
        {unlimited
          ? `${formatBytes(m.totalBytes)} · ${t('settings.storage.chatStorageUnlimited')}`
          : blocked
            ? `${formatBytes(m.totalBytes)} · ${t('settings.storage.chatStorageBlockedShort')}`
            : t('settings.storage.chatStorageOf', {
                used: formatBytes(m.totalBytes),
                limit: formatBytes(m.limitBytes ?? 0),
              })}
      </span>
      {/* No `onAdjust` means a read-only caller (D13): the whole menu goes,
          rather than opening onto an action that would 403. The column keeps
          its width so the rows still line up. */}
      <span className="relative shrink-0 w-6">
        {onAdjust && (<>
        <button
          type="button"
          aria-label={t('settings.storage.chatStorageAdjustLimit')}
          onClick={() => setMenuOpen((o) => !o)}
          className="w-6 h-6 rounded-md flex items-center justify-center themed-text-secondary hover:themed-bg-tertiary"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
          </svg>
        </button>
        {menuOpen && (
          <span className="absolute top-[26px] right-0 z-10 block min-w-[170px] rounded-md themed-bg-tertiary themed-border border shadow-lg py-0.5">
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onAdjust(m) }}
              className="block w-full text-left px-3.5 py-1.5 text-[11px] themed-text hover:themed-bg-secondary"
            >
              {t('settings.storage.chatStorageAdjustLimit')}
            </button>
            {/* THE CURE FOR THE BADGE, on the row the badge is on. The
                design's own words: "a member tagged SCHEDULED already has
                a cleanup coming; cancel it from their own row first." */}
            <CleanupMenuItems
              scheduled={scheduled}
              onCancelScheduled={onCancelScheduled}
              onCleanUp={onCleanUp ? () => onCleanUp(m) : undefined}
              close={() => setMenuOpen(false)}
            />
          </span>
        )}
        </>)}
      </span>
    </StorageRow>
  )
}

/**
 * Bytes whose uploader cannot be resolved, as a row like any other.
 *
 * It is NOT a member, so there is no avatar, no track and no limit — a
 * promise needs somebody to make it to. Everything else is the member row:
 * the same columns, the same ⋮, the same cleanup.
 *
 * Season's point (2026-10-15), which overturned an earlier objection of
 * mine: an admin never sees anyone's files anyway, and the cleanup is "how
 * long has this been idle", so whether the uploader can be named changes
 * nothing about whether it is safe to clear. A conversation a current
 * member is still using will not be idle; one that is idle is exactly what
 * their own rule would have taken.
 *
 * The label says UNKNOWN rather than "no longer a member": this row also
 * holds pre-0304 rows the backfill could not resolve, whose uploader may
 * very well still work here. Claiming they left would be stating something
 * we have not established.
 */
function UnattributedRow({ bytes, canManage, onCleanUp, scheduled, onCancelScheduled, selected, onSelect }: {
  bytes: number
  canManage: boolean
  onCleanUp: () => void
  /** Ticked for the bulk Clean up, like a member (Season 2026-10-22: it
   *  could only be cleaned up on its own, so "clear everything idle" took
   *  two goes and this row was the one forgotten). */
  selected?: boolean
  onSelect?: (next: boolean) => void
  /** Season (2026-10-17): a target like any member, so it badges and
   *  cancels like one. */
  scheduled?: LiveCleanup
  onCancelScheduled?: (id: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const [menuOpen, setMenuOpen] = useState(false)
  return (
    <StorageRow>
      {onSelect && (
        // Same rule as a member: a booking already live cannot be ticked.
        <input
          type="checkbox"
          checked={!!selected}
          disabled={!!scheduled}
          onChange={(e) => onSelect(e.target.checked)}
          aria-label={t('settings.storage.chatStorageUnattributed')}
          className="shrink-0"
        />
      )}
      <span className="w-6 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-[13.5px] italic themed-text-muted">
        {t('settings.storage.chatStorageUnattributed')}
      </span>
      {scheduled && <StatusDot tone="info" active={scheduled.state === 'running'} label={t(scheduled.state === 'running'
        ? 'settings.storage.cleanupRunningBadge'
        : 'settings.storage.cleanupScheduledBadge')} />}
      <span className="hidden lg:block shrink-0" style={{ width: TRACK_PX }} />
      <span className={`${FIGURE_COL} themed-text-muted`}>{formatBytes(bytes)}</span>
      {/* Round 4, finding 6 — this row had its OWN ⋮ and its own Force clean
          up, took no capability, and so stayed fully live for a read-only
          caller while every other control on the section was withheld. Inert
          today, but the stacked PR makes it a destructive action over bytes
          nobody owns, and most real orgs have some (a departed member, a fork
          of their conversation, any pre-0304 row the backfill could not
          resolve). Same `w-6` wrapper as MemberRow so the column lines up
          either way. */}
      <span className="relative shrink-0 w-6">
        {canManage && (<>
        <button
          type="button"
          aria-label={t('settings.storage.chatStorageForceCleanUp')}
          onClick={() => setMenuOpen((o) => !o)}
          className="w-6 h-6 rounded-md flex items-center justify-center themed-text-secondary hover:themed-bg-tertiary"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
          </svg>
        </button>
        {menuOpen && (
          <span className="absolute top-[26px] right-0 z-10 block min-w-[170px] rounded-md themed-bg-tertiary themed-border border shadow-lg py-0.5">
            {/* No Adjust limit — there is nobody to give a limit to. The
                cleanup is the same action every other row gets: Season's
                point was that the window is what makes it safe, and the
                window does not care whether the uploader can be named. */}
            <CleanupMenuItems
              scheduled={scheduled}
              onCancelScheduled={onCancelScheduled}
              onCleanUp={onCleanUp}
              close={() => setMenuOpen(false)}
            />
          </span>
        )}
        </>)}
      </span>
    </StorageRow>
  )
}

/**
 * The member's own figure, and what it looks like once they are OVER.
 *
 * Being over is not a louder version of being nearly full — it is a
 * different fact with a different next step, so it gets the big number,
 * the hatched bar and a sentence that says the three things a member in
 * that state actually asks: am I blocked (yes), did I lose anything (no),
 * and how much do I have to clear (this much). "92% full" answers none of
 * them.
 *
 * Asking an admin is offered here because it is the member's ONLY other
 * move: their own page can free what they put there, and when the
 * organisation is full even that does nothing.
 */
function MyFigure({ member, tenantId, canAsk = true }: {
  member: MemberChatStorageRow
  tenantId: string
  /** False for someone who can raise their OWN limit — see the mount. */
  canAsk?: boolean
}) {
  const { t } = useTranslation('core-ui')
  const [asking, setAsking] = useState(false)
  const [asked, setAsked] = useState<'sent' | 'pending' | null>(null)
  const [askFailed, setAskFailed] = useState<'unreachable' | 'nobody' | null>(null)

  const limit = member.limitBytes
  // A 0 limit is a BLOCK whether or not anything is stored yet, so it is
  // `over` too (scoped review of full review 3, orgs finding 6). The
  // first cut keyed `blocked` inside the `over` branch, and `over` needs
  // `total > limit`, which 0 > 0 is not — so the member who will be
  // refused on their very next upload got the calm grey line "0 B of
  // 0 B", while the admin's list drew that same person a full red bar.
  // The two surfaces have to agree about who is in trouble.
  const blocked = limit === 0
  // EXACTLY AT the limit is blocked too. The gate refuses at
  // `total + net > limit`, so a member on 500 MB holding exactly 500 MB
  // cannot attach anything — and `MemberRow` (ratio >= 1) and the in-chat
  // banner both already draw them red. This page, which the banner's
  // "Manage chat storage" button sends them to, showed a calm cyan dot
  // and "500 MB of 500 MB": nothing wrong, no explanation, and no
  // "Ask admin" button, because that lives inside this branch (full
  // review 4C, finding 2). Its own sibling comment already states the
  // rule — the surfaces have to agree about who is in trouble.
  const full = limit !== null && limit > 0 && member.totalBytes === limit
  const over = blocked || full || (limit !== null && member.totalBytes > limit)
  // A percentage OF zero is meaningless — the pill used to read
  // "Over limit — 0%" for someone storing 50 MB against a 0 B limit.
  // Said in words instead (full review 3C, finding 3).
  const pct = limit && limit > 0 ? Math.round((member.totalBytes / limit) * 100) : 0

  const ask = async () => {
    setAsking(true)
    setAskFailed(null)
    try {
      const r = await storageApi.requestMoreChatStorage(tenantId)
      // `notified === 0` is a FAILURE for this member, even though the
      // route returns ok (scoped review round 3, finding 3). Per-recipient
      // catches mean one unreachable admin no longer 500s the request —
      // good — but they also mean "nobody could be reached" now arrives
      // as a success, and saying "your admins have been told" while
      // disabling the button leaves them waiting for an answer nobody was
      // asked for. That is the rule this file's own catch below states.
      if (!r.alreadyPending && r.notified === 0) {
        setAsked(null)
        // Two different sentences, but do not read the common one as
        // "this is transient". Owner is an absolute capability floor, so
        // `eligible === 0` needs an org with no owner membership AND
        // every admin explicitly denied — rare. The other branch covers
        // both a transient failure and a membership whose address has no
        // account row (itself a partial-failure state, not an everyday
        // one — see `notifyUserByEmail`), which is why that copy says to
        // try again OR reach them another way rather than promising a
        // retry will work.
        setAskFailed(r.eligible === 0 ? 'nobody' : 'unreachable')
        return
      }
      setAsked(r.alreadyPending ? 'pending' : 'sent')
    } catch {
      // Left as not-asked: a failed request that said "sent" would leave
      // the member waiting for an answer nobody was asked for.
      setAsked(null)
      setAskFailed('unreachable')
    } finally {
      setAsking(false)
    }
  }

  if (!over) {
    // NEARLY full is its own state, and it is the one worth a sentence.
    // Over the limit the member is already stopped and the panel below
    // explains it; at 92% nothing on screen has told them that the next
    // upload may be the one that fails, and the two things they can do
    // about it are not obvious — especially that MOVING a file to the
    // Drive frees this quota, which is the move that loses them nothing.
    const nearlyFull = limit !== null && limit > 0 && member.totalBytes / limit >= 0.8
    const headroom = limit === null ? 0 : Math.max(0, limit - member.totalBytes)
    return (
      <div className="space-y-2 py-1.5">
        <div className="flex items-center gap-2.5">
          <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${nearlyFull ? 'bg-amber-500' : 'bg-cyan-500'}`} />
          <span className="grow text-sm themed-text">
            {limit === null
              ? `${formatBytes(member.totalBytes)} · ${t('settings.storage.chatStorageUnlimited')}`
              : t('settings.storage.chatStorageOf', { used: formatBytes(member.totalBytes), limit: formatBytes(limit) })}
          </span>
        </div>
        {nearlyFull && (
          <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <span className="text-amber-500 shrink-0">&#9888;</span>
            <p className="text-[13px] leading-relaxed themed-text">
              {t('settings.storage.nearlyFullTitle', { free: formatBytes(headroom) })}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3 py-1">
      <div className="flex items-baseline gap-2.5 flex-wrap">
        <span className="text-[26px] font-semibold text-rose-500">{formatBytes(member.totalBytes)}</span>
        <span className="text-sm themed-text-muted">
          {t('settings.storage.chatStorageOfLimit', { limit: formatBytes(limit!) })}
        </span>
        <span className="text-[13px] font-bold text-rose-500 bg-rose-500/15 rounded-full px-2.5 py-0.5">
          {blocked
            ? t('settings.storage.chatStorageBlockedPill')
            : full
              ? t('settings.storage.atLimitPill')
              : t('settings.storage.overLimitPill', { pct })}
        </span>
      </div>
      {/* Hatched, for all three of the states that reach this panel:
          over the limit, exactly AT it, and a limit of 0. The hatch means
          "you cannot upload", not "you have overflowed" — the earlier
          wording drew the distinction the other way, and `full` is now in
          here precisely because exactly-at is not survivable either
          (scoped review of the convergence pass, finding 9). */}
      <div className="h-4 rounded-lg overflow-hidden ws-storage-cannot-upload" />
      <div className="flex items-start gap-2.5 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="shrink-0 mt-0.5">
          <circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 16h.01" />
        </svg>
        <p className="text-[13px] leading-relaxed themed-text">
          <strong>{t('settings.storage.overLimitTitle')}</strong>
          <br />
          {/* A 0 limit has nothing to free, so the usual body — "free at
              least {{free}} before you can upload again" — rendered
              "free at least 0 B", which is both false and useless as a
              next step (scoped review of full review 3 part 2, finding
              3). Its own sentence, pointing at the only move they have. */}
          {/* Three different states, three sentences. "Free at least
              {{free}}" is the right advice only when there IS an overage
              to clear: at exactly the limit it reads "free at least
              0 B", and at a limit of 0 there is nothing to free at all. */}
          {/* A DIFFERENT SENTENCE for the member who is the admin. Hiding
              "Ask for space" from someone who can grant it is right, but
              the body above still said "ask an admin for space" — and for
              a 0 limit it also rules out the only other action, so they
              were left with no next step at all and no hint that they are
              that admin (scoped review round 35, finding 2). */}
          {blocked
            ? t(canAsk ? 'settings.storage.blockedLimitBody' : 'settings.storage.blockedLimitBodySelf')
            : full
              ? t(canAsk ? 'settings.storage.atLimitBody' : 'settings.storage.atLimitBodySelf', { limit: formatBytes(limit!) })
              // "You are N over it — free MORE than that", not "free at
              // least N". The gate refuses at `total + net > limit`, so
              // clearing exactly the overage lands them on 500/500, which
              // this same component calls blocked two branches up. The
              // figure is the overage either way; only the instruction
              // built on it was off by the last byte (final full review,
              // C6 — same root as the Adjust dialog's equality case).
              : t('settings.storage.overLimitBody', {
                  limit: formatBytes(limit!),
                  free: formatBytes(member.totalBytes - limit!),
                })}
        </p>
      </div>
      <div className="flex items-center gap-2.5 flex-wrap">
        {canAsk && (
        <button
          type="button"
          onClick={ask}
          disabled={asking || asked !== null}
          className="text-[13px] px-3.5 py-2 rounded-lg themed-border border themed-text font-semibold disabled:opacity-60"
        >
          {t('settings.storage.askForSpace')}
        </button>
        )}
        {asked ? (
          <span className="text-xs themed-text-muted">
            {t(asked === 'pending' ? 'settings.storage.askForSpacePending' : 'settings.storage.askForSpaceSent')}
          </span>
        ) : askFailed ? (
          // The button stays live — that is the point. Saying nothing at
          // all would be the silent failure this branch exists to avoid.
          <span className="text-xs text-red-500">
            {t(askFailed === 'nobody' ? 'settings.storage.askForSpaceNobody' : 'settings.storage.askForSpaceFailed')}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * A per-file delete answered 404: the file's ledger row is already gone —
 * released meanwhile by the recount or the member's own rule. (The route
 * also answers 404 when the workspace database will not open; see the
 * residual in `MySessionFileRow`'s delete.)
 */
function alreadyGone(e: unknown): boolean {
  return (e as { status?: number } | null)?.status === 404
}

/**
 * Files deleted this visit whose space the server could not release yet
 * (D-a). Held by the PAGE, not the row: the rules dialog's "Delete (N)"
 * deletes through the same route, and its line sends the member to the
 * list's Sync again — which a row-local flag would not have shown for a
 * file the dialog deleted. Not persisted: after a reload such a row is a
 * plain row again, and its Delete does the same retry.
 */
const SpacePendingFiles = createContext<{ has: (id: string) => boolean; mark: (id: string, pending: boolean) => void } | null>(null)

function useSpacePendingFilesState() {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set())
  const mark = useCallback((id: string, pending: boolean) => setIds((prev) => {
    if (prev.has(id) === pending) return prev
    const next = new Set(prev)
    if (pending) next.add(id); else next.delete(id)
    return next
  }), [])
  return useMemo(() => ({ has: (id: string) => ids.has(id), mark }), [ids, mark])
}

/**
 * Which of the member's conversations are open, and whether "show more"
 * was pressed — held by the PAGE, not the rows (D-g). Every refetch (after
 * Delete, Move, a rule change) swaps the list for the loading skeleton,
 * and the rows remounting forgot this: the conversation the member was
 * working in folded up under them, hiding a file left on Sync again.
 *
 * ⚠️ ONLY THIS IS KEPT. The skeleton swap stays, because other state in
 * the list relies on the remount to start fresh — an admin's "Declined"
 * marks, a member's "request sent", another view's rows. Keeping the whole
 * list mounted instead (the first cut) left those stuck: a member's NEW
 * request drawn as already declined (full review round 1).
 */
const ListViewMemory = createContext<{ get: (key: string) => boolean; set: (key: string, on: boolean) => void } | null>(null)

function useListViewMemoryState() {
  const [on, setOn] = useState<ReadonlySet<string>>(() => new Set())
  const set = useCallback((key: string, value: boolean) => setOn((prev) => {
    if (prev.has(key) === value) return prev
    const next = new Set(prev)
    if (value) next.add(key); else next.delete(key)
    return next
  }), [])
  const clear = useCallback(() => setOn(new Set()), [])
  return useMemo(() => ({ get: (key: string) => on.has(key), set, clear }), [on, set, clear])
}

/** A boolean that outlives the list's remount — see `ListViewMemory`. */
function useRemembered(key: string): [boolean, (next: boolean | ((was: boolean) => boolean)) => void] {
  const memory = useContext(ListViewMemory)
  const [own, setOwn] = useState(false)
  const value = memory ? memory.get(key) : own
  const update = (next: boolean | ((was: boolean) => boolean)) => {
    const v = typeof next === 'function' ? next(value) : next
    if (memory) memory.set(key, v); else setOwn(v)
  }
  return [value, update]
}

/**
 * One conversation in the member's own list, and the only control on this
 * page that actually frees space.
 *
 * ── Why Delete lives here at all ──────────────────────────────────────
 *
 * A blocked member has exactly two moves: ask an admin for more, or clear
 * something. Until now this page offered only the first. The list already
 * answered "where is my space going"; without a Delete beside the answer
 * the member has to leave Settings, find each conversation in the chat, and
 * delete it there — which is the same seven steps behind the same route.
 *
 * ── Why some rows have no Delete, and that is not a bug ───────────────
 *
 * The list is grouped by `uploaded_by_email`, so it also holds conversations
 * belonging to SOMEBODY ELSE that this member uploaded a file into. The
 * server marks the member's own by returning a `workspaceId` — it sets that
 * only for a conversation their own `Session.list` returned, the same filter
 * the chat list uses. It is an ownership PROOF and not a convenience:
 * `DELETE /sessions/:sessionID` does not check whose conversation it is
 * (`Session.remove` is `WHERE id = ?`, no `user_email` arm — filed, not
 * fixed here, as it predates this branch). Rendering a Delete on every row
 * would hand a member a colleague's session id and a button to use it.
 *
 * Those rows keep their bytes and their figure. They are the member's bytes;
 * it is the conversation that is not theirs to remove.
 *
 * ── The confirm ───────────────────────────────────────────────────────
 *
 * `useConfirmOptional` with a `window.confirm` fallback, the same shape
 * `MiniAppSettingsModifier` uses: this component is rendered by tests
 * outside a `ConfirmProvider`, and in jsdom `window.confirm` returns
 * undefined — falsy — so a test can never delete by accident.
 */
/**
 * One of the member's own conversations, and the files inside it.
 *
 * ── Why it opens ──────────────────────────────────────────────────────
 *
 * A member near their limit needs to drop the 200 MB video and keep the
 * thread. With only "Delete conversation" the choice is all-or-nothing,
 * which is how people end up keeping work they do not want rather than
 * losing work they do.
 *
 * ── The two numbers on a file are different on purpose ────────────────
 *
 * `size` is what the file is; `chargedBytes` is what it costs THEM. A
 * second reference to bytes they already uploaded, or bytes that are also
 * a Drive file, cost nothing — the quota dedupes and the list has to say
 * so, or deleting a 0 MB row looks broken when the figure does not move.
 * That is the "shared with …, takes no extra space" line.
 *
 * ── Move to Drive is not a copy ───────────────────────────────────────
 *
 * It moves the bytes into the Drive and leaves the conversation pointing
 * at the Drive file, so the chat quota is freed and nothing is lost. It
 * needs a CAS handle; inline bytes live in the message part and have
 * none, so those rows offer Delete only.
 */
function MySessionFileRow({ s, f, openIn, onChanged, ruleDueAt = null }: {
  s: ChatStorageSessionRow
  f: ChatStorageFileRow
  /** When the member's own file-age rule deletes this file (0353), or
   *  `null` when that is more than `RULE_WARN_DAYS` away. The conversation
   *  stays, so this is the FILE's dot, not the conversation's. */
  ruleDueAt?: number | null
  /** Where things on this row open — see `OpenIn`. */
  openIn: OpenIn
  onChanged: () => void
}) {
  const { t } = useTranslation('core-ui')
  const confirmDialog = useConfirmOptional()
  const [busy, setBusy] = useState<'move' | 'delete' | 'sync' | null>(null)
  const [failed, setFailed] = useState(false)
  // #1397: a Move refused because the file's stored copy is damaged — the server's sentence (which file, and what to
  // do), never only "Failed"
  const [damaged, setDamaged] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  /** Deleted, but the server could not release its space (D-a): the row
   *  stays listed at full size until the recount, so it says why and
   *  offers Sync again instead of a Delete that has already happened.
   *  Page-wide, so a file the rules dialog deleted shows it too. */
  const pendingFiles = useContext(SpacePendingFiles)
  const [ownPending, setOwnPending] = useState(false)
  const spacePending = pendingFiles ? pendingFiles.has(f.id) : ownPending
  const setSpacePending = (on: boolean) => { if (pendingFiles) pendingFiles.mark(f.id, on); else setOwnPending(on) }

  const move = async () => {
    if (!s.workspaceId) return
    setBusy('move'); setFailed(false); setDamaged(null)
    try {
      // By FILE ROW, not by handle — a pasted image has no handle, and
      // keying on one is why those rows used to have no Move button.
      await storageApi.moveChatFileToDrive(s.sessionId, s.workspaceId, f.id)
      onChanged()
    } catch (err) { setDamaged(damagedMessageFrom(err)); setFailed(true) } finally {
      // CLEARED EITHER WAY. It used to rely on the refetch unmounting
      // the row — which a CAS row does not do, so a successful move left
      // the button stuck on "Moving…" until a reload.
      setBusy(null)
    }
  }

  const remove = async () => {
    if (!s.workspaceId) return
    const message = t('settings.storage.fileDeleteConfirm', { name: f.name, size: formatBytes(f.chargedBytes) })
    const ok = confirmDialog
      ? await confirmDialog({ title: t('settings.storage.fileDeleteTitle'), message, type: 'danger', confirmText: t('settings.storage.chatStorageDelete') })
      : window.confirm(message)
    if (!ok) return
    await sendDelete('delete')
  }

  /** The delete itself — also what Sync again sends. On a file already
   *  deleted the route writes nothing (the part is the note now, so it
   *  matches no file) and only retries releasing the space. No confirm
   *  for that: nothing more is removed. */
  const sendDelete = async (as: 'delete' | 'sync') => {
    if (!s.workspaceId) return
    setBusy(as); setFailed(false); setDamaged(null)
    try {
      const r = await storageApi.deleteChatStorageFile(s.sessionId, s.workspaceId, f.id)
      // Not released: the page-wide mark swaps the actions for the
      // pending label and Sync again (D-a) — the row comes back from the
      // refetch, which remounts it. Released: the refetch takes the row.
      if (r.spacePending) { setSpacePending(true); setBusy(null) }
      onChanged()
    } catch (e) {
      // 404: the ledger row is already gone — the recount or the member's
      // own rule released it meanwhile — so the file is gone, which IS the
      // outcome asked for, not a failure (full review round 1). Sync again,
      // and Delete on such a row after a reload (scoped review). The
      // refetch takes the row. Residual, stated: the route also answers 404
      // when the workspace database will not open; then the row comes back
      // with no word — a database error at that moment, under the bar.
      if (alreadyGone(e)) { onChanged(); return }
      setFailed(true); setBusy(null)
    }
  }

  /**
   * WHY THIS ROW IS CHARGED 0 — and the answer is not always "another
   * conversation has it".
   *
   * The same file attached TWICE to the SAME conversation makes two
   * `session_attachments` rows: the upload's idempotency key is per
   * attach attempt (`att_<time>_<n>`), not per content, so nothing
   * collapses them. The listing's dedup then charges the first and
   * zeroes the second, and the second's "first reference" is this very
   * conversation — so the line read "Shared with <this conversation>",
   * which is not a sentence anybody can act on.
   *
   * Said plainly instead. The row STAYS (see the list's own note on why
   * it is not hidden), because the two rows can carry DIFFERENT NAMES —
   * the same bytes attached under two filenames — and because deleting
   * the charged one frees nothing while the other still references the
   * blob.
   */
  const duplicateHere = f.sharedWithSessionId === s.sessionId
  const sharedLabel = duplicateHere
    ? t('settings.storage.fileDuplicateInThisChat')
    : f.sharedWithTitle
      ? t('settings.storage.fileSharedWith', { title: f.sharedWithTitle })
      : t('settings.storage.fileSharedWithOther')

  /** When it was uploaded — the figure the member's own file-age rule is
   *  measured from, so the list has to be able to show it. */
  const uploadedDay = formatDateWithYear(f.uploadedAt)
  const uploadedTitle = formatTimestamp(f.uploadedAt)

  /** Its bytes are a Drive file now: charged nothing, and nothing left
   *  to move. Inline rows are never in this state — they are charged in
   *  full until they are moved or deleted. */
  const alreadyInDrive = f.leg === 'cas' && f.chargedBytes === 0 && !f.sharedWithSessionId

  // ⚠️ WHICH ORIGIN THIS LINK NEEDS, and the answer is not the same on
  // every deployment. Two mechanisms serve `/api` and they cover different
  // hosts:
  //
  //  * a same-origin Cloudflare Pages Function (`functions/_proxy.ts`)
  //    forwards `/api/*` for golink, stagetest, stagetest2 and every per-PR
  //    preview — and locally vite's proxy does the same;
  //  * `getApiBase()` derives the API host at runtime for everything.
  //
  // PRODUCTION (`os.papan.ai`) IS NOT IN THAT PROXY'S LIST. So a bare
  // `/api/...` href resolved against the static frontend and the file
  // simply did not open — reported on production, 2026-10-22, and
  // invisible everywhere else, which is why it shipped.
  //
  // But a blanket absolute URL is not the fix either: a per-PR preview is
  // `*.pages.dev` against `*-api.papan.ai`, which is CROSS-SITE, so a
  // top-level navigation drops the partitioned session cookie and the API
  // answers 401 — turning a working link there into a broken one. So the
  // rule is SAME-SITE ONLY: go direct when the API shares the page's
  // registrable domain (production, stagetest), and otherwise stay
  // relative and let the proxy that does exist there carry it.
  //
  // ⚠️ AND BOTH LEGS OPEN NOW. A CAS attachment goes to the chat's own
  // handle route; an INLINE one (every image and every PDF — see
  // `isInlinableMedia`) has no handle and had nothing to open, so its name
  // rendered as plain text. It now has a route of its own, keyed on the
  // file row exactly as Delete and Move to Drive already are.
  const apiBase = getApiBase()
  const linkBase = sameSiteApiBase(apiBase, typeof window === 'undefined' ? '' : window.location.hostname)
  // ⚠️ THE TENANT AND THE IDENTITY GO IN THE QUERY, and leaving them out is
  // how this link 404'd in every ORGANISATION while working on a personal
  // drive — the shape it was tested on.
  //
  // `@papan/api-client` puts `X-Tenant-Id` and `X-Org-Email` on the fetches
  // it makes. A browser following an `<a href>` sends neither, and
  // `resolveTenant` then falls back to the caller's PERSONAL tenant
  // (`resolveActiveTenantIdentity`: no requested tenant → `defaultTenantId`).
  // The handler looks for the member's workspace inside their personal
  // tenant, finds nothing, and the new tab reads "Project not found". On a
  // personal drive the default IS the page's tenant, which is why it looked
  // right. The middleware accepts `?tenantId=` / `?orgEmail=` for exactly
  // this case.
  //
  // The EMAIL matters too: a member holding two addresses in one org and no
  // stored selection is answered 401 `org_email_required` without it.
  // `s.workspaceId` is non-null here: `openUrl` below returns null without
  // it before this is used.
  const linkScope = `?workspaceId=${encodeURIComponent(s.workspaceId ?? '')}`
    + `&tenantId=${encodeURIComponent(openIn.tenantId)}`
    + (openIn.orgEmail ? `&orgEmail=${encodeURIComponent(openIn.orgEmail)}` : '')
  // ⚠️ `openHandle`, NEVER `f.handle`, and the difference was a dead link
  // on every file over the 10 MB inline budget (owner, 2026-10-30).
  //
  // The comment this replaces said "the handle is what the CAS route
  // takes". It is not: `f.handle` is the CONTENT HASH, and that route
  // resolves a `display_handle` (`getByHandle`). So the member clicked
  // their own 14 MB PDF and got a 404 — on a page whose whole job is to
  // show them what they are holding. `f.openHandle` is the addressable
  // one; `f.handle` stays the identity the "takes no extra space" line
  // is built on, and the two are different values for the same file.
  //
  // The fallthrough is deliberate and worth stating plainly: a cas row
  // that arrives WITHOUT an open handle — an API from before the field
  // existed, mid-deploy — goes to the per-file route, finds no
  // `inline_attachments` row, and 404s. That is the safe direction; the
  // alternative is a url with `undefined` in its path.
  const openUrl = !s.workspaceId
    ? null
    : f.openHandle
      ? `${linkBase}/api/agent/sessions/${encodeURIComponent(s.sessionId)}/attachments/${encodeURIComponent(f.openHandle)}${linkScope}`
      : `${linkBase}/api/agent/sessions/${encodeURIComponent(s.sessionId)}/chat-storage/files/${encodeURIComponent(f.id)}${linkScope}`

  return (
    <>
    <div className="flex items-center gap-2.5 py-1.5 pl-7 text-[13px]">
      {spacePending ? (
        // DELETED, so not a link (fresh full review round 2). Its ledger
        // row is still there, so a CAS link would stream the whole file
        // from a row that says it is gone, and an inline one would answer
        // 409 as raw JSON — the part is the note now.
        <span className="min-w-0 flex-1 truncate themed-text-muted line-through">{f.name}</span>
      ) : openUrl ? (
        <a
          href={openUrl}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 flex-1 truncate themed-text hover:underline"
          title={t('settings.storage.fileOpen')}
        >
          {f.name}
        </a>
      ) : (
        // Only reachable with NO workspace on the row — a conversation
        // whose workspace this member cannot resolve. ⚠️ AND THE TOOLTIP
        // IS OPTIMISTIC: the conversation-name button is gated on the same
        // `s.workspaceId`, so "open it in the chat" is the one thing that
        // cannot work here either. What is actually available to them is
        // nothing. Left as it is rather than invented: naming the real
        // state needs a copy decision and the honest sentence is probably
        // about the conversation, not the file.
        <span className="min-w-0 flex-1 truncate themed-text" title={t('settings.storage.fileOpenInChat')}>{f.name}</span>
      )}
      {/* THE DEDUP DISCLOSURE. Without it a 0 MB row beside a real file
          reads as a bug, and deleting it frees nothing — which is worse.
          On a narrow screen it folds into the `title` of the size, which
          is the figure it is explaining: it is context for "why 0 B",
          and it must not be what pushes the actions off the row. */}
      {f.sharedWithSessionId && (
        <span className="cs-hide-t1 shrink-0 text-[11px] themed-text-muted truncate max-w-[18rem]">
          {sharedLabel}
        </span>
      )}
      {ruleDueAt !== null && (
        // The same dot a conversation wears when it is going, on the one
        // FILE that is going — the conversation stays. Bare, with the
        // sentence in `title`, for the same reason as the conversation
        // row's: the name is this row's only flexible cell.
        <StatusDot
          tone="danger"
          bare
          label={t('settings.storage.cleanupGoesBadge')}
          // Already in the Drive: there is no Move on this row, so the
          // advice to move it would point at a button that is not there —
          // and the Drive copy means nothing is lost (full review round 5).
          title={alreadyInDrive
            ? (ownRuleDaysLeft(ruleDueAt) > 0
                ? t('settings.storage.ownRuleGoesFileInDriveIn', { count: ownRuleDaysLeft(ruleDueAt) })
                : t('settings.storage.ownRuleGoesFileInDrive'))
            : ownRuleDaysLeft(ruleDueAt) > 0
              ? t('settings.storage.ownRuleGoesFileIn', { count: ownRuleDaysLeft(ruleDueAt) })
              : t('settings.storage.ownRuleGoesFile')}
        />
      )}
      {failed && !damaged && <span className="shrink-0 text-[11px] text-rose-500">{t('settings.storage.fileActionFailed')}</span>}
      {/* WHEN IT WAS UPLOADED. From `md` up, like the file count and the
          dedup line beside it, and by the LIST's own width rather than
          the window's: this list lives in the Settings content column,
          so a viewport breakpoint is true while the row has no room.
          This row's only flexible column is the file's NAME, and
          anything added to the fixed side takes width from it. The full
          date and time ride in the `title`, and a row with no date (an
          API from before the field, or a stored value that would not
          parse) renders nothing rather than 1970. */}
      {uploadedDay && (
        <span
          className="cs-hide-t3 shrink-0 tabular-nums themed-text-muted text-[11px] w-[5.5rem] text-right"
          title={uploadedTitle ? t('settings.storage.fileUploadedOn', { date: uploadedTitle }) : undefined}
        >
          {uploadedDay}
        </span>
      )}
      <span
        className="shrink-0 tabular-nums themed-text-muted w-16 text-right"
        title={[
          f.sharedWithSessionId ? sharedLabel : null,
          // On a narrow screen the date is not rendered, so it folds in
          // here with the figure it belongs beside.
          uploadedTitle ? t('settings.storage.fileUploadedOn', { date: uploadedTitle }) : null,
          f.isSnapshot ? t('settings.storage.fileSnapshotHint') : null,
        ].filter(Boolean).join(' · ') || undefined}
      >
        {formatBytes(f.chargedBytes)}
      </span>
      {s.workspaceId && alreadyInDrive && (
        // ALREADY IN THE DRIVE. The row stays — the conversation still
        // points at the file — but it costs nothing here and there is
        // nothing to move. Offering Move writes a SECOND Drive copy
        // under a suffixed name and re-points nothing, while answering
        // ok: a member who reads "0 B" as "that did not work" makes a
        // copy per press.
        //
        // ⚠️ ONLY MOVE GOES. Delete stays below, because the server
        // zeroes this charge for a second reason too — ANY Drive file in
        // the tenant with the same bytes, whoever put it there. Somebody
        // who uploaded a file to their Drive last year and later
        // attached it in chat lands here having moved nothing, and
        // hiding Delete would leave them unable to remove the
        // attachment from their own conversation at all.
        <span className="shrink-0 text-[11px] themed-text-muted">{t('settings.storage.fileInDrive')}</span>
      )}
      {f.isSnapshot && (
        // AN AGENT SCREENSHOT has no Delete and no Move (full review
        // round 1, finding 1): the transcript points at it from a tool
        // result, not a file part, so neither action could leave the
        // conversation making sense. It goes with its conversation.
        // Folded away at the same tier as the actions it stands in for;
        // the size's hover text carries the same sentence.
        <span className="cs-hide-t2 shrink-0 text-[11px] themed-text-muted" title={t('settings.storage.fileSnapshotHint')}>
          {t('settings.storage.fileSnapshot')}
        </span>
      )}
      {spacePending && (
        // In place of both actions, at every tier: the file IS deleted
        // (the conversation already says so), so a Delete here would be
        // the wrong thing to offer. Sync again retries the release —
        // the recount would too, but only after hours, and the worker
        // never recounts a conversation database over its size cap (only
        // the host script does).
        <>
          <span className="shrink-0 text-[11px] themed-text-muted cursor-help" title={t('settings.storage.fileSpacePendingHint')}>
            {t('settings.storage.fileSpacePending')} ⓘ
          </span>
          {s.workspaceId && (
            <button
              type="button"
              onClick={() => { void sendDelete('sync') }}
              disabled={busy !== null}
              className="shrink-0 text-[11px] px-2 py-0.5 rounded-md border themed-border themed-text-muted disabled:opacity-60"
            >
              {busy === 'sync' ? t('settings.storage.fileSyncing') : t('settings.storage.fileSyncAgain')}
            </button>
          )}
        </>
      )}
      {s.workspaceId && !f.isSnapshot && !spacePending && (
        <>
          {/* WIDE: both actions, named. Offered for BOTH legs — a pasted
              screenshot is exactly what somebody near their limit wants
              to keep without paying for, and the server materialises its
              bytes so the one save path still does the work. */}
          {/* ⚠️ BY THE LIST'S WIDTH (0353). These two named buttons are
              ~180px against a title that is the only flexible cell, so
              a viewport breakpoint kept them while the row had no room.
              `flex` is Tailwind's and always applies; the unlayered
              `.cs-hide-t2` takes it away below the tier — the same trick
              `.drive-cq` uses. */}
          <span className="flex items-center gap-2 shrink-0 cs-hide-t2">
            {!alreadyInDrive && (
            <button
              type="button"
              onClick={() => { void move() }}
              disabled={busy !== null}
              className="text-[11px] px-2 py-0.5 rounded-md border border-blue-500/60 text-blue-300 hover:bg-blue-500/10 disabled:opacity-60"
            >
              {busy === 'move' ? t('settings.storage.fileMoving') : t('settings.storage.fileMoveToDrive')}
            </button>
            )}
            <button
              type="button"
              onClick={() => { void remove() }}
              disabled={busy !== null}
              className="text-[11px] px-2 py-0.5 rounded-md border border-rose-500/40 text-rose-400 disabled:opacity-60"
            >
              {busy === 'delete' ? t('settings.storage.chatStorageDeleting') : t('settings.storage.chatStorageDelete')}
            </button>
          </span>
          {/* NARROW: the same two behind a ⋮. Two named buttons plus a
              filename do not fit a phone, and what happened before was
              that they ran off the edge — present but unreachable, which
              is worse than a menu. CSS, not a resize listener: the
              breakpoint is the only thing deciding, so there is no first
              render at the wrong size. */}
          {/* The ⋯ menu those two fold into, at the same tier. */}
          <span className="cs-show-t2 relative shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              disabled={busy !== null}
              aria-label={t('settings.storage.fileActions', { name: f.name })}
              className="w-6 h-6 rounded-md flex items-center justify-center themed-text-secondary disabled:opacity-60"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
              </svg>
            </button>
            {menuOpen && (
              <span className="absolute top-[26px] right-0 z-10 block min-w-[150px] rounded-md themed-bg-tertiary themed-border border shadow-lg py-0.5">
                {!alreadyInDrive && (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); void move() }}
                  className="block w-full text-left px-3.5 py-1.5 text-[11px] themed-text hover:themed-bg-secondary"
                >
                  {t('settings.storage.fileMoveToDrive')}
                </button>
                )}
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); void remove() }}
                  className="block w-full text-left px-3.5 py-1.5 text-[11px] text-rose-400 hover:themed-bg-secondary"
                >
                  {t('settings.storage.chatStorageDelete')}
                </button>
              </span>
            )}
          </span>
        </>
      )}
    </div>
    {/* Its own line: a sentence does not fit beside a filename and two buttons */}
    {damaged && <p role="alert" className="pl-7 pb-1.5 text-[11px] text-rose-500">{damaged}</p>}
    </>
  )
}

/**
 * A conversation row: collapsed by default, opening onto its files.
 *
 * `goesOnCleanup` is computed by the caller from the booking's own
 * criteria, so the badge and the banner cannot disagree about which
 * conversations are caught.
 */
/**
 * Where a row's "open this" goes, derived once where the tenant and the
 * member's identity are both in scope and passed down unchanged.
 *
 * Two different reasons, which is why all three are here:
 *
 *  * `urlPrefix` is the SPA's own tenant routing — `/t/<id>` or `/~` — and
 *    the chat link is a navigation within the app;
 *  * `tenantId` and `orgEmail` are for the FILE links, which the browser
 *    follows to the API directly, carrying none of the headers
 *    `@papan/api-client` puts on its own fetches. Without them
 *    `resolveTenant` falls back to the caller's personal tenant and the
 *    workspace is not there — a 404 in every organisation.
 *
 * `orgEmail` serves both: the chat link also uses it, as the key the
 * queued tab is written under.
 */
type OpenIn = {
  urlPrefix: string
  orgEmail?: string
  tenantId: string
}

/**
 * The API origin to put in a LINK the browser will navigate to, or '' to
 * stay relative. See the call site for why both answers are needed.
 *
 * Same site = same registrable domain. Compared on the last two labels,
 * which is exact for the hosts this product has (`papan.ai`,
 * `papan-4dj.pages.dev` — whose registrable domain is `pages.dev`, so a
 * `*.pages.dev` page and an `*.papan.ai` API correctly compare as
 * different). A longer public-suffix list is not worth carrying for a
 * decision whose fallback ('' — the same-origin proxy) is the safe one.
 */
function sameSiteApiBase(apiBase: string, pageHostname: string): string {
  if (!apiBase) return ''
  let apiHost: string
  try { apiHost = new URL(apiBase).hostname } catch { return '' }
  const site = (h: string) => h.split('.').slice(-2).join('.')
  return site(apiHost) && site(apiHost) === site(pageHostname) ? apiBase : ''
}

/**
 * Open a conversation in the chat.
 *
 * There is no `/chat/:sessionId` route — which conversations are open is
 * deck state — so the ids ride in the query (`?ws=&session=`) and the deck
 * reads them on arrival (`useOpenConversationFromUrl`).
 *
 * ⚠️ `window.location.assign`, NOT the router, and the reason changed with
 * this branch. It used to be "a full load is what guarantees the chat
 * reads the tab state we just wrote" — but the tab queue is not what opens
 * the conversation any more. What the full load now buys is a REMOUNT: the
 * desktop deck is kept mounted and merely hidden (`display:none`) once
 * chat has been opened, and a hook that has already run with no parameters
 * has latched. A router navigate would change the URL under a deck that
 * will not look at it again.
 */
function openConversation(
  s: ChatStorageSessionRow,
  where: { urlPrefix: string; orgEmail?: string },
  chatOn: boolean,
): void {
  if (!s.workspaceId) return
  // ⚠️ THE EMAIL IS NOT OPTIONAL IN AN ORGANISATION. The queue is a
  // localStorage entry whose key is `…-<workspaceId>-<email>` when an email
  // is given and `…-<workspaceId>` when it is not — and the chat READS it
  // as `useOpenTabs(workspaceId, orgEmail)`. Queuing without the email
  // wrote to a key the chat never looks at, so the tab was never opened.
  // Still queued: the open-tab list is what a WORKSPACE view's `ChatPanel`
  // reads, so a member who opens that project later finds the conversation
  // already on a tab. It is NOT what opens it here — this deck keeps its
  // own selection — which is why the link below carries the ids.
  queueSessionTab(s.workspaceId, s.sessionId, where.orgEmail)
  // ⚠️ AND THIS TENANT'S CHAT, not the personal one. `/~` is the personal
  // space; an organisation's is `/t/<tenantId>` (both routes exist). Hard-
  // coding `/~/chat` threw a member who clicked their own conversation in
  // an org out into their personal space — which is why it asked them to
  // pick an account and then sat on an empty chat: the conversation is not
  // there to open. Reported on production, 2026-10-22.
  // ⚠️ THE IDS GO IN THE URL. Navigating to the bare chat deck opened the
  // default assistant and nothing else: the queue above is read by a
  // different surface (`ChatPanel`, inside a workspace view), and the chat
  // home never looks at it. `?ws=&session=` is what that deck now reads on
  // arrival; it opens the conversation and strips them again.
  // No chat home on this deployment (a launcher landing, #368): open the
  // conversation's WORKSPACE instead — its AI panel reads the tab queued
  // above. By FOLDER id (`workspaceId` is the record id no project route
  // opens); a conversation from the old chat home opens in the hidden system
  // workspace it lives in (owner, 2026-10-29).
  if (!chatOn) {
    if (s.workspaceFolderId) window.location.assign(`${where.urlPrefix}/workspace/${encodeURIComponent(s.workspaceFolderId)}`)
    return
  }
  const q = `ws=${encodeURIComponent(s.workspaceId)}&session=${encodeURIComponent(s.sessionId)}`
  window.location.assign(`${where.urlPrefix}/chat?${q}`)
}

function MySessionRow({ s, branches, goesOnCleanup, branchesOf, caughtBy, dotTitleOf, freedBytes, starLocked = false, kept = false, ruleConvDueAt, ruleFileDueAt, openIn, onChanged, listName = 'main' }: {
  s: ChatStorageSessionRow
  /** Which list this row is in, for `ListViewMemory`: a conversation on a
   *  booking is listed twice (main list and scheduled list), and one key
   *  for both opened the other copy too (scoped review of round 1). */
  listName?: 'main' | 'scheduled'
  /** Conversations branched off this one, shown indented under it. */
  branches: ChatStorageSessionRow[]
  goesOnCleanup: boolean
  /** When the member's OWN idle rule takes this (0353), or `null` when it
   *  is not within `RULE_WARN_DAYS` of doing so. Kept apart from
   *  `goesOnCleanup`, which is a BOOKING: the star lock and `keptByStar`
   *  read `caughtBy` and assume a booking. Passed down so each branch gets
   *  its own verdict. */
  ruleConvDueAt?: (s: ChatStorageSessionRow) => number | null
  /** The same for one file, under the member's OWN file-age rule. */
  ruleFileDueAt?: (fileId: string) => number | null
  /** Passed down so a branch can render its OWN branches — a fork of a
   *  fork is still one of the member's conversations. */
  branchesOf: (id: string) => ChatStorageSessionRow[]
  /** Passed down so each row gets its OWN verdict rather than its
   *  parent's. */
  caughtBy: (s: ChatStorageSessionRow) => boolean
  /** The red dot's hover text — the date it goes, and why a star does not
   *  help when it does not (Season 2026-10-22: the date lives here, not
   *  on the row). Passed down for the same reason as `caughtBy`. */
  dotTitleOf?: (s: ChatStorageSessionRow) => string | undefined
  /** How many bytes deleting this conversation ACTUALLY frees — which is
   *  not its size when it holds a file that also sits in another of her
   *  conversations (the charge moves to the other copy). Computed by the
   *  LIST, the only place that can see those: a shared file's other copy
   *  is just as likely to be below the fold. Passed down for the same
   *  reason as `caughtBy`. */
  freedBytes: (s: ChatStorageSessionRow) => number
  /** Every cleanup that catches this row ignores stars, so a star cannot
   *  keep it. Shown greyed rather than hidden — the page's rule for a
   *  control that exists but does nothing for you here. Only the
   *  scheduled list sets it: on the main list the star still governs the
   *  member's own auto-delete rule. */
  starLocked?: boolean
  /** On her own booking's list and kept ONLY by its star. Shown, dimmed,
   *  so starring does not make the row vanish as if it had been deleted —
   *  and so the star can be taken off again from here. */
  kept?: boolean
  /** Where things on this row open — see `OpenIn`. */
  openIn: OpenIn
  onChanged: () => void
}) {
  const { t } = useTranslation('core-ui')
  const chatOn = useChatHomeAvailable()
  const confirmDialog = useConfirmOptional()
  const [open, setOpen] = useRemembered(`open:${listName}:${s.sessionId}`)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  /** Why there is no Delete here — see the arm that renders it. An older
   *  server sends no reason, and then the row keeps the dash it has always
   *  had rather than guessing which of the two it is. */
  const unconfirmedText = s.workspaceId
    ? undefined
    : s.unconfirmed === 'workspace-unreadable'
      ? t('settings.storage.chatStorageUnconfirmedUnreadable')
      : s.unconfirmed === 'workspace-missing'
        ? t('settings.storage.chatStorageUnconfirmedMissing')
        : s.unconfirmed === 'gone'
          ? t('settings.storage.chatStorageUnconfirmedGone')
          : undefined
  const [starred, setStarred] = useState(s.starred)
  // ⚠️ IN STEP WITH THE PROP. The row is keyed by session id, so a
  // refetch updates it in place and never remounts — and since the
  // badge's wording began reading `s.starred` (so it could not contradict
  // the dot), the optimistic local copy became the only thing on the row
  // that can disagree with the server. Star a conversation in the chat
  // tab, come back, cause any refetch here, and the tooltip would say
  // "even though you starred it" beside a hollow ☆.
  useEffect(() => { setStarred(s.starred) }, [s.starred])

  // ⚠️ A RESPONSE FROM AN OLDER SERVER HAS NO `files` ARRAY — that field
  // used to be the file COUNT. During a rolling deploy this component is
  // served to browsers talking to an instance that has not restarted yet,
  // and `s.files.length` on a number throws, which React renders as
  // "something went wrong" over the whole page rather than one stale row.
  // Degrading to "a row that will not open" is the right failure.
  const files = Array.isArray(s.files) ? s.files : []
  const fileCount = typeof s.fileCount === 'number' ? s.fileCount : files.length
  /** The member's own idle rule takes this soon — see the prop. */
  const ownRuleDue = ruleConvDueAt?.(s) ?? null
  const takenByOwnRule = ownRuleDue !== null
  const ownRuleLeft = ownRuleDue !== null ? ownRuleDaysLeft(ownRuleDue) : 0
  /** Files in here the member's file rule takes soon, while the row is
   *  folded and their own dots cannot be seen (Season 2026-10-30). Only
   *  when the conversation itself is not going: its dot already covers
   *  everything in it. */
  const filesGoInside = !open && !goesOnCleanup && !takenByOwnRule &&
    files.some((f) => (ruleFileDueAt?.(f.id) ?? null) !== null)
  /** When the conversation was last used. `null` for a row whose
   *  conversation this request could not resolve — see `formatDateWithYear`. */
  const lastUsedDay = formatDateWithYear(s.updatedAt)
  const lastUsedTitle = formatTimestamp(s.updatedAt)

  const remove = async () => {
    if (!s.workspaceId) return
    // (see `unconfirmedText` above for the no-workspace arm)
    // A conversation holding none of her files (one listed only because a
    // booking takes it) frees nothing, and "gives you back 0 B" reads as a
    // glitch on a delete confirmation.
    // And one whose files are all counted elsewhere (a second reference, or
    // already a Drive file) frees nothing either, for a different reason.
    // ⚠️ A SIZE IS NOT A PROMISE. The member's CAS bytes are deduped across
    // their own conversations, so the row showing 500 MB is the HOLDER of a
    // file that may also sit in another chat — delete it and the other copy
    // becomes the holder, the charge moves, and the total does not change.
    // That is todo item 4: "I deleted a 500 MB conversation and the number
    // did not move", right after this dialog said it would give her 500 MB
    // back. Said only when it is true of this conversation; when nothing is
    // shared, the figure really is what she gets.
    // ⚠️ THE FIGURE IS WHAT SHE GETS BACK, not what the row is charged.
    // A shared file is charged HERE only because this conversation is the
    // first reference — delete it and the charge moves to the other copy,
    // so those bytes never come back. Promising the charged figure and
    // then explaining it in a footnote explains a wrong number instead of
    // fixing it (Season, 2026-10-23).
    const freed = freedBytes(s)
    const message = freed === 0
      ? t(fileCount === 0 ? 'settings.storage.chatStorageDeleteBodyNoFiles' : 'settings.storage.chatStorageDeleteBodyFreesNothing')
      : t('settings.storage.chatStorageDeleteBody', { size: formatBytes(freed) })
        // ⚠️ A SECOND PARAGRAPH NAMING THE REMAINDER (Season, 2026-10-23).
        // The question she is actually asking is "the list said 500 MB, why
        // does this say 120?" — so the answer carries the difference as a
        // figure rather than a general caveat. `whitespace-pre-line` on the
        // modal's body is what makes the blank line a paragraph break.
        //
        // Only when the two differ: with nothing shared there is no
        // remainder to explain, and a caveat there would teach her to
        // distrust a figure that is exact.
        + (freed < s.bytes
            ? '\n\n' + t('settings.storage.chatStorageDeleteBodySharesFiles', { size: formatBytes(s.bytes - freed) })
            : '')
    const ok = confirmDialog
      ? await confirmDialog({
          title: t('settings.storage.chatStorageDeleteTitle'),
          message, type: 'danger', confirmText: t('settings.storage.chatStorageDelete'),
        })
      : window.confirm(message)
    if (!ok) return
    setBusy(true); setFailed(false)
    try {
      await storageApi.deleteMyChatStorageSession(s.sessionId, s.workspaceId)
      onChanged()
    } catch { setFailed(true); setBusy(false) }
  }

  const toggleStar = async () => {
    if (!s.workspaceId) return
    const next = !starred
    setStarred(next)   // optimistic, so the star reacts to the press
    try {
      await storageApi.setChatStorageSessionStarred(s.sessionId, s.workspaceId, next)
      // ⚠️ AND RELOAD, because a star is not just a star on this page.
      //
      // The red "will be deleted" dot and the banner's count both come
      // from the parent's `sessions` prop, and local state does not
      // touch it — so a member who read "6 conversations will be
      // deleted" and starred the three they need watched three stars
      // turn gold while the three red dots and the number 6 stayed
      // exactly as they were. Starring is the ONE action the notice
      // window exists for, and it was the one with no feedback that it
      // had worked.
      onChanged()
    } catch {
      setStarred(!next)
    }
  }

  return (
    <>
      <div className={`flex items-center gap-2.5 py-1.5 text-sm ${kept ? 'opacity-60' : ''}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={files.length === 0}
          aria-expanded={open}
          className="shrink-0 w-4 text-xs themed-text-muted disabled:opacity-30"
        >
          {open ? '⌄' : '›'}
        </button>
        {/* Where the deployment has no chat home (#368) the link opens the
            conversation's workspace, so it needs that workspace's folder. */}
        {s.workspaceId && s.title && (chatOn || s.workspaceFolderId) ? (
          <button
            type="button"
            onClick={() => openConversation(s, openIn, chatOn)}
            className="min-w-0 flex-1 truncate text-left themed-text hover:underline"
            title={t('settings.storage.openConversation')}
          >
            {s.title}
          </button>
        ) : (
          // No workspace (not theirs) or no title (it could not be
          // resolved) — there is nothing to open, so it is not a link.
          <span className={`min-w-0 flex-1 truncate ${s.title ? 'themed-text' : 'themed-text-muted italic'}`}>
            {s.title ?? t('settings.storage.chatStorageUnknownConversation')}
          </span>
        )}
        {kept && (
          <span className="shrink-0 text-[10px] font-bold tracking-wider text-amber-400">
            {t('settings.storage.cleanupKeptBadge')}
          </span>
        )}
        {(goesOnCleanup || takenByOwnRule) && (
          <StatusDot
            tone="danger"
            bare
            label={t('settings.storage.cleanupGoesBadge')}
            // ⚠️ A STARRED ROW THAT IS STILL GOING NEEDS A DIFFERENT
            // SENTENCE. After the server's star filter, the only way one
            // can be marked is a cleanup that overrides stars — possibly
            // somebody else's, whose flag the banner above cannot see.
            // Without it the member reads a gold star, a red dot and
            // "starred conversations are kept" at once, unstars and
            // re-stars, and nothing moves.
            //
            // ⚠️ FROM THE PROP, NOT THE LOCAL `starred` STATE. The local
            // one is set optimistically by `toggleStar`, while whether
            // this badge appears at all comes from the server snapshot —
            // so between the press and the refetch they disagreed, and
            // the row told a member who had just starred a conversation
            // on a star-HONOURING booking that starring would not save
            // it. That is the one second in the whole feature when she
            // is deciding whether to copy 2 GB out. One snapshot decides
            // both halves.
            //
            // It rides in `title`, not the visible slot: this label sits
            // in a fixed-width row whose only flexible element is the
            // conversation's name, and a sentence here truncates the
            // name away next to a Delete button.
            //
            // A BOOKING'S dot keeps its date. Only a row taken by the
            // member's own rule ALONE says so — and how many days are
            // left, worked out from the rule (Season 2026-10-30).
            title={goesOnCleanup
              ? (dotTitleOf?.(s) ?? (s.starred ? t('settings.storage.cleanupGoesBadgeStarred') : undefined))
              : ownRuleLeft > 0
                ? t('settings.storage.ownRuleGoesConvIn', { count: ownRuleLeft })
                : t('settings.storage.ownRuleGoesConv')}
          />
        )}
        {filesGoInside && (
          <StatusDot
            tone="danger"
            bare
            label={t('settings.storage.cleanupGoesBadge')}
            title={t('settings.storage.filesInsideGo')}
          />
        )}
        <span className="cs-hide-t2 shrink-0 themed-text-muted text-xs">
          {t('settings.storage.chatStorageFileCount', { count: fileCount })}
        </span>
        {/* WHEN IT WAS LAST USED — the figure the rule for idle
            conversations is measured from, so the list has to be able to
            show it. Dropped FIRST of the optional cells (tier 1), because
            it was added last and because "Delete conversation" alone is
            ~250px of this row — adding a column under a VIEWPORT
            breakpoint collapsed the title to under one character at
            768–830px, which is what the container tiers fix. A row whose
            conversation could not be resolved carries `updatedAt: 0` and
            renders nothing rather than 1970. */}
        {lastUsedDay && (
          <span
            className="cs-hide-t3 shrink-0 tabular-nums themed-text-muted text-xs w-[5.5rem] text-right"
            title={lastUsedTitle ? t('settings.storage.chatStorageLastUsed', { date: lastUsedTitle }) : undefined}
          >
            {lastUsedDay}
          </span>
        )}
        {s.workspaceId && (
          <button
            type="button"
            onClick={() => { void toggleStar() }}
            disabled={starLocked}
            aria-pressed={starred}
            title={starLocked
              ? t('settings.storage.cleanupStarLocked')
              : t(starred ? 'settings.storage.chatStorageUnstar' : 'settings.storage.chatStorageStar')}
            className={`shrink-0 text-sm disabled:opacity-35 disabled:cursor-not-allowed ${starred ? 'text-amber-400' : 'themed-text-muted'}`}
          >
            {starred ? '★' : '☆'}
          </button>
        )}
        <span className="shrink-0 themed-text tabular-nums text-xs w-20 text-right">{formatBytes(s.bytes)}</span>
        {failed && <span className="shrink-0 text-xs text-rose-500">{t('settings.storage.chatStorageDeleteFailed')}</span>}
        <span className="shrink-0 text-right">
          {s.workspaceId ? (
            <button
              type="button"
              onClick={() => { void remove() }}
              disabled={busy}
              className="text-xs px-2.5 py-1 rounded-lg bg-rose-600 text-white font-semibold disabled:opacity-60 whitespace-nowrap"
            >
              {/* "Delete conversation" is two words too many for a narrow
                  list, and the row it belongs to already names the
                  conversation.
                  ⚠️ BY THE LIST'S WIDTH, not the window's (0353). This is
                  the WIDEST cell on the row — roughly 250px against a
                  title that is the only flexible one — so a viewport
                  breakpoint kept the long label while the row had no room
                  for it. It folds FIRST (tier 1), before the file count:
                  720px was too low, and the title stayed squeezed through
                  the whole 720–900 band — which is where an ordinary
                  window puts this list. */}
              {busy
                ? t('settings.storage.chatStorageDeleting')
                : (<>
                    <span className="cs-hide-t1">{t('settings.storage.chatStorageDeleteConversation')}</span>
                    <span className="cs-show-t1">{t('settings.storage.chatStorageDelete')}</span>
                  </>)}
            </button>
          ) : (
            // Said out loud rather than left blank: a missing button with
            // no reason reads as a glitch, and the reason is the point.
            // ⚠️ "COULD NOT CONFIRM", NOT "SOMEONE ELSE'S" (#1280 review
            // #11). Only a conversation's owner can write into it, so every
            // conversation on her page is hers.
            //
            // ⚠️ AND THE TWO REASONS WANT OPPOSITE ADVICE (todo 3). One
            // sentence used to cover both: "it may have just been deleted,
            // or couldn't be read. Try again later." For a conversation
            // that is GONE, "try again later" sends her to do something
            // that can never succeed — the row is a ledger leftover and
            // the recount sweep clears it, with nothing for her to do.
            //
            // In the ROW, not only in a tooltip: hover does not exist on a
            // phone, and this is the one thing that explains the missing
            // button.
            <span
              className="text-xs themed-text-muted text-right block max-w-[16rem] leading-snug"
              title={unconfirmedText}
            >
              {unconfirmedText ?? <>&mdash;</>}
            </span>
          )}
        </span>
      </div>
      {open && files.map((f) => (
        <MySessionFileRow key={f.id} s={s} f={f} openIn={openIn} onChanged={onChanged} ruleDueAt={ruleFileDueAt?.(f.id) ?? null} />
      ))}
      {/* A BRANCH is its own conversation with its own files and its own
          Delete — indented so it reads as having come from the one above,
          which is what a fork is.
          Its OWN branches, and its OWN verdict: passing `branches={[]}`
          made a branch of a branch vanish from the page while its bytes
          stayed in the figure above, and passing the parent's
          `goesOnCleanup` marked a branch created yesterday as going —
          and left an 18-month-old one unmarked, and then took it. */}
      {branches.map((b) => (
        <div key={b.sessionId} className="pl-6 themed-border border-l ml-2">
          <MySessionRow
            s={b}
            branches={branchesOf(b.sessionId)}
            goesOnCleanup={caughtBy(b)}
            branchesOf={branchesOf}
            caughtBy={caughtBy}
            dotTitleOf={dotTitleOf}
            freedBytes={freedBytes}
            ruleConvDueAt={ruleConvDueAt}
            ruleFileDueAt={ruleFileDueAt}
            openIn={openIn}
            onChanged={onChanged}
            listName={listName}
          />
        </div>
      ))}
    </>
  )
}


/**
 * A row's cleanup actions in its ⋮ menu — ONE copy for the member row and
 * the uploader-unknown row (#1280 review #9: they were pasted twice, and
 * D35 step ④ then added Stop to each by hand).
 *
 * Which one depends on the row's live booking: a RUNNING one is stopped
 * (D35 step ④ — the same cancel call; the server stops it after the batch
 * in hand and tells the member), and a viewer who cannot stop it gets a
 * line rather than an empty menu; a BOOKED one is cancelled — the cure for
 * the badge, on the row the badge is on; otherwise, the cleanup itself.
 */
function CleanupMenuItems({ scheduled, onCancelScheduled, onCleanUp, close }: {
  scheduled?: LiveCleanup
  onCancelScheduled?: (id: string) => void
  onCleanUp?: () => void
  close: () => void
}) {
  const { t } = useTranslation('core-ui')
  const item = 'block w-full text-left px-3.5 py-1.5 text-[11px] hover:themed-bg-secondary'
  if (scheduled?.state === 'running') {
    return onCancelScheduled ? (
      <button type="button" onClick={() => { close(); onCancelScheduled(scheduled.id) }} className={`${item} text-rose-400`}>
        {t('settings.storage.cleanupStopRunning')}
      </button>
    ) : (
      <span className="block px-3.5 py-1.5 text-[11px] themed-text-muted">
        {t('settings.storage.cleanupRunningMenuNote')}
      </span>
    )
  }
  if (scheduled && onCancelScheduled) {
    return (
      <button type="button" onClick={() => { close(); onCancelScheduled(scheduled.id) }} className={`${item} themed-text`}>
        {t('settings.storage.cleanupCancelScheduled')}
      </button>
    )
  }
  return onCleanUp ? (
    <button type="button" onClick={() => { close(); onCleanUp() }} className={`${item} text-rose-400`}>
      {t('settings.storage.chatStorageForceCleanUp')}
    </button>
  ) : null
}

/**
 * A row's state as a coloured dot, with its words beside it on a wide
 * screen and only the dot on a narrow one (Season, 2026-10-17).
 *
 * Three badges competing for the same line — OVER LIMIT, SCHEDULED,
 * CUSTOM — pushed the figure off the row on anything under a laptop. The
 * information is worth keeping (an admin scans this list for exactly
 * these three things), so it shrinks rather than going away: the colour
 * carries it, the text explains it where there is room, and the `title`
 * explains it where there is not.
 *
 * A dot alone is not self-explanatory, which is why there is a legend
 * under the list. The tooltip is the fallback, not the design.
 */
function StatusDot({ tone, label, title, bare = false, active = false }: {
  tone: 'danger' | 'info' | 'muted'
  /** Something is HAPPENING now — a cleanup being carried out. The dot
   *  pulses, so below `xl`, where the word is hidden, a running cleanup no
   *  longer looks exactly like a booked one (#1280 review #7). */
  active?: boolean
  /** The dot alone at every width — the member's conversation rows, where
   *  the date in `title` is the useful part and the word only repeated
   *  what the dot already says (Season 2026-10-22). Screen readers still
   *  get the word. */
  bare?: boolean
  /** The short word shown in the row. Kept short on purpose — see below. */
  label: string
  /**
   * A longer explanation, on hover and for a screen reader, when the
   * short word is not the whole story.
   *
   * ⚠️ IT MUST NOT GO IN `label`. This badge sits in a row whose only
   * flexible element is the conversation's NAME, and this whole
   * component exists because three short badges were already squeezing
   * it. A sentence in the visible slot truncates the name to nothing,
   * directly beside a Delete button.
   */
  title?: string
}) {
  // ⚠️ THE ROW ALREADY HAS A CYAN BAR ON IT, and the first cut made the
  // scheduled dot blue — adjacent enough that on a row whose bar is cyan
  // the dot read as part of the bar (Season, 2026-10-17). Indigo is far
  // enough from cyan to tell apart at 8 pixels, and is not one of the By
  // type legend's colours above (blue documents, emerald images, violet
  // video, amber miniapp, zinc other, teal reclaimable, cyan chat
  // storage, rose duplicates) — a status must never borrow one of those.
  //
  // `danger` deliberately SHARES rose with the full bar: there they mean
  // the same thing, and agreeing is the point.
  //
  // `muted` is a HOLLOW ring rather than a faint fill. A filled grey dot
  // was too quiet to notice at all, and simply brightening it would have
  // put a third loud dot beside two that are warnings — "this person has
  // their own limit" is information, not an alarm. An outline is crisper
  // than a soft fill at this size and reads as a different KIND of thing.
  const shape = tone === 'danger'
    ? 'bg-rose-500'
    : tone === 'info'
      ? 'bg-indigo-400'
      // `border-current` on a themed text colour, NOT a fixed zinc. This
      // app themes through CSS variables, not Tailwind's `dark:` variant
      // — a literal `zinc-300` ring is bright on the dim theme and
      // invisible on the light one, which is the same "too faint"
      // complaint again, just on a different theme.
      : 'border-[1.5px] border-current themed-text-secondary'
  return (
    <span className="shrink-0 inline-flex items-center gap-1" title={title ?? label}>
      <span className={`w-2.5 h-2.5 rounded-full ${shape}${active ? ' animate-pulse' : ''}`} aria-hidden="true" data-active={active || undefined} />
      {/* The words, where the row can afford them. `sr-only` below that
          width so a screen reader still gets them — a bare dot is
          nothing at all to one. */}
      {/* `aria-hidden` so the visible word is not announced TWICE
          alongside the screen-reader text below, which already contains
          it. */}
      {!bare && <span aria-hidden="true" className="hidden xl:inline text-[10px] font-bold tracking-wider themed-text-secondary">{label}</span>}
      {/* The short word at every width — the legend under the list
          explains the colours BY that word, so dropping it left a
          screen-reader user on a narrow screen with nothing to match —
          plus the long form when there is one, because the dot cannot
          convey it and a hover tooltip reaches nobody without a mouse. */}
      <span className="sr-only">{title ? `${label}. ${title}` : label}</span>
    </span>
  )
}

/** Collapsed to this many; the rest sit behind one line that says what
 *  is in them, because the thing a member needs from the tail is "is any
 *  of it about to go", not the titles. */
const MY_INITIAL_ROWS = 5

/**
 * The member's own conversations — the half of the page they can act on.
 *
 * ── Which conversations a booking catches is computed HERE ────────────
 *
 * The server sends the booking's CRITERIA, not a list. The same rule
 * applied to the same rows cannot disagree with itself, and it costs no
 * second scan of every workspace database to draw a banner. (The deletion
 * re-evaluates at run time, so this is a forecast — the copy says "will
 * be deleted", which is what a booking is.)
 *
 * ── The star caveat is the important sentence on this page ────────────
 *
 * A star bypasses the member's OWN auto-delete rule. An admin's cleanup
 * can be told to ignore stars, and when it has been, saying nothing would
 * leave somebody starring conversations all week to protect them and
 * losing them anyway. So the banner says it plainly and names the number.
 */
/**
 * The member's OWN auto-delete rule, and the one-off version of it
 * (#758 part 2, the design's "Delete conversations idle for more than N
 * · Preview and delete · Keep doing this automatically").
 *
 * ── Two controls, one number ──────────────────────────────────────────
 *
 * `Preview and delete` does it once, now. The checkbox turns the same
 * number into a standing rule. They share the field because they are the
 * same decision asked twice — "this long is too long for me" — and
 * splitting them into two numbers is how somebody ends up with a rule
 * they did not realise differed from the thing they just previewed.
 *
 * ── Why the preview is not optional ───────────────────────────────────
 *
 * "30 days" means nothing until you see which conversations that is.
 * Everything else on this page deletes one named thing the member is
 * looking at; this is the only control that deletes a set, so it shows
 * the set first.
 *
 * ── A star is what this rule is for ───────────────────────────────────
 *
 * It always skips starred conversations — unlike the admin's cleanup,
 * which can be told to ignore them. The line under the control says so,
 * and `Session.setStarred` holds the idle clock so starring never
 * changes whether something is caught.
 */
/**
 * What both of the member's own rules start at, before they have saved one
 * (Season, 2026-10-30: 90 for both). ONE constant, so the two controls
 * that sit one above the other cannot drift apart — they used to start at
 * 30 and 365, which read as two unrelated settings rather than one
 * decision asked about two kinds of thing.
 *
 * It only PRE-FILLS the field. Nothing is deleted until the member presses
 * Delete (which confirms, naming the count) or ticks Run automatically.
 */
const DEFAULT_RULE_DAYS = 90

/**
 * How far ahead the member's own rules MARK what they will take (Season,
 * 2026-10-30: "3 天前"). The rule itself deletes only what is past its
 * threshold; the dots start this many days earlier so there is time to
 * star a conversation or move a file to the Drive before it goes.
 */
const RULE_WARN_DAYS = 3
const DAY_MS = 24 * 60 * 60 * 1000

/** When the idle rule becomes able to take `s`: the LAST of it and its
 *  ancestors to go idle, because the rule never takes a branch whose
 *  parent is still in use (see `convsTakenByIdleRule`). */
function convRuleDueAt(s: ChatStorageSessionRow, byId: Map<string, ChatStorageSessionRow>, days: number): number {
  let last = s.updatedAt
  const seen = new Set<string>([s.sessionId])
  let p = s.parentId
  while (p && !seen.has(p)) {
    seen.add(p)
    const parent = byId.get(p)
    if (!parent) break
    last = Math.max(last, parent.updatedAt)
    p = parent.parentId
  }
  return last + days * DAY_MS
}

/** Whole days until the member's own rule can take something, `0` once
 *  it is due — then the hover text says "at its next run" instead. Rounded
 *  UP, so "in 1 day" never means "in two hours"; the rule runs about
 *  daily, so it can go a day later than this, never earlier. */
function ownRuleDaysLeft(dueAt: number): number {
  return Math.max(0, Math.ceil((dueAt - Date.now()) / DAY_MS))
}

/**
 * The conversations the member's own IDLE rule takes — the ONE predicate
 * behind both the dialog's "Delete (N)" and the red dot on every row it
 * will take once the rule is on. A preview and the thing it previews must
 * not be two pieces of code, or the dot and the count drift apart.
 *
 * Owned, idle longer than `days`, not starred — AND NOT HANGING OFF
 * SOMETHING STILL IN USE.
 *
 * ⚠️ THE ANCESTOR PART IS NOT OPTIONAL. A branch of a thread somebody used
 * yesterday is itself months "idle" (its clock froze when that turn
 * ended), so without it the preview offered — and a dot would mark — a
 * branch the server refuses, because the server refuses any row whose
 * ancestor is not going too. The page already has the tree, so this is
 * the same walk the server does.
 */
function convsTakenByIdleRule(sessions: ChatStorageSessionRow[], days: number): ChatStorageSessionRow[] {
  const byId = new Map(sessions.map((x) => [x.sessionId, x]))
  const idleEnough = (s: ChatStorageSessionRow) =>
    !!s.workspaceId && !s.starred && s.updatedAt > 0 &&
    Date.now() - s.updatedAt > days * 24 * 60 * 60 * 1000
  return sessions.filter((s) => {
    if (!idleEnough(s)) return false
    const seen = new Set<string>([s.sessionId])
    let p = s.parentId
    while (p) {
      if (seen.has(p)) return false      // a parent loop proves nothing
      seen.add(p)
      const parent = byId.get(p)
      // ⚠️ A PARENT THIS PAGE CANNOT SEE IS A PARENT THAT IS KEEPING IT.
      //
      // The page lists conversations by who UPLOADED into them, so a
      // parent holding none of THIS member's files is simply absent — and
      // that is exactly the case the server refuses, because its own rule
      // for the member only reaches conversations with their files in.
      //
      // The residual, stated: a parent row genuinely DELETED from the
      // workspace is also absent here, and the server would allow that
      // child. Blocking it under-counts in a case that needs a parent
      // deleted without its branch, which nothing on this page can do.
      if (!parent || !idleEnough(parent)) return false
      p = parent.parentId
    }
    return true
  })
}

/**
 * The files the member's own FILE-AGE rule takes — the ONE predicate
 * behind the dialog's "Delete (N)" and the red dot on every file row it
 * will take once the rule is on.
 *
 * ⚠️ `uploadedAt` MAY BE ABSENT — an API that predates it, or a stored
 * timestamp that would not parse. Such a file is LEFT OUT rather than
 * assumed old: under-counting means the preview promises less than the
 * rule does, while the other way round would offer to delete files nobody
 * can see the age of.
 *
 * A STAR keeps a conversation's files, as the rule does — through the
 * ancestors the page can see. Residual, stated: a starred parent holding
 * none of this member's files is not on this page, so its branch's files
 * are counted here while the rule skips them.
 *
 * NEVER A FILE ALREADY DELETED whose space is pending (D-a): it stays
 * listed until its release goes through, but there is nothing left to
 * delete — counting it offered "Delete (N)" over content already gone and
 * gave its row a dot saying the rule will delete it (full review round 1).
 */
function filesTakenByAgeRule(
  sessions: ChatStorageSessionRow[], days: number,
  pending: { has: (id: string) => boolean } | null,
): Array<{ sessionId: string; workspaceId: string; file: ChatStorageFileRow }> {
  const byId = new Map(sessions.map((x) => [x.sessionId, x]))
  const starredInChain = (s: ChatStorageSessionRow) => {
    const seen = new Set<string>()
    let at: ChatStorageSessionRow | undefined = s
    while (at && !seen.has(at.sessionId)) {
      if (at.starred) return true
      seen.add(at.sessionId)
      at = at.parentId ? byId.get(at.parentId) : undefined
    }
    return false
  }
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
  return sessions.flatMap((s) => {
    if (!s.workspaceId || starredInChain(s)) return []
    return s.files
      // Never an agent screenshot: the rule skips them (full review
      // round 1, finding 1), so neither may the preview or the dots.
      .filter((f) => !f.isSnapshot && typeof f.uploadedAt === 'number' && f.uploadedAt < cutoff && !pending?.has(f.id))
      .map((f) => ({ sessionId: s.sessionId, workspaceId: s.workspaceId as string, file: f }))
  })
}

function MyAutoDeleteControl({ sessions, savedDays, onChanged }: {
  sessions: ChatStorageSessionRow[]
  savedDays: number | null
  onChanged: () => void
}) {
  const { t } = useTranslation('core-ui')
  const confirmDialog = useConfirmOptional()
  const [text, setText] = useState(String(savedDays ?? DEFAULT_RULE_DAYS))
  const [auto, setAuto] = useState(savedDays !== null)
  // RESYNC when the saved rule changes under us. The parent refetches
  // after every delete, and without this the field keeps whatever was
  // last typed while the sentence under it reads the saved value — two
  // numbers on screen describing one rule.
  const lastSaved = useRef(savedDays)
  useEffect(() => {
    if (lastSaved.current === savedDays) return
    lastSaved.current = savedDays
    setText(String(savedDays ?? DEFAULT_RULE_DAYS))
    setAuto(savedDays !== null)
  }, [savedDays])
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  const days = /^\d+$/.test(text.trim()) && Number(text.trim()) >= 1 && Number(text.trim()) <= 3650
    ? Number(text.trim())
    : null

  // The same predicate the red dot on each row uses — see
  // `convsTakenByIdleRule` for the rule and why the ancestors matter.
  const caught = days === null ? [] : convsTakenByIdleRule(sessions, days)
  const caughtBytes = caught.reduce((n, s) => n + s.bytes, 0)

  const previewAndDelete = async () => {
    // The guard this replaces returned only on the missing-dialog
    // branch, so `days === null` fell through and interpolated `null`
    // into the confirm copy. Harmless only because the button happened
    // to be disabled on exactly those conditions — i.e. it was load
    // bearing on something else being right.
    if (days === null || caught.length === 0) return
    const message = t('settings.storage.autoDeleteConfirmBody', {
      count: caught.length, size: formatBytes(caughtBytes), days,
    })
    const ok = confirmDialog
      ? await confirmDialog({
          title: t('settings.storage.autoDeleteConfirmTitle', { count: caught.length }),
          message, type: 'danger', confirmText: t('settings.storage.autoDeleteConfirmYes'),
        })
      : window.confirm(message)
    if (!ok) return
    setBusy(true); setFailed(false)
    // #1316 review round 2 (owner, 2026-10-28): the bulk delete the person confirmed FINISHES as the identity that
    // confirmed it — every delete goes as it, never as the next session; nothing is shown once that session ended
    const batch = pinnedBatch()
    try {
      // One at a time, through the SAME delete the row's own button
      // uses — not a bulk endpoint. A second way to delete a
      // conversation is a second place for the seven ordered steps to
      // drift.
      for (const s of caught) {
        if (!s.workspaceId) continue
        const workspaceId = s.workspaceId
        await batch.run(() => storageApi.deleteMyChatStorageSession(s.sessionId, workspaceId))
      }
    } catch {
      if (!batch.ended()) setFailed(true)
    } finally {
      // IN `finally`, because a loop that threw on item 4 of 9 still
      // deleted three. Calling it only on success left the member
      // looking at a red error and a list that still showed the
      // conversations which had gone, with their figure unchanged until
      // they reloaded the page.
      onChanged()
      setBusy(false)
    }
  }

  const toggleAuto = async (next: boolean) => {
    if (next && days === null) return
    setAuto(next)
    setFailed(false)
    try {
      await storageApi.setChatAutoDelete(next ? days : null)
      // TELL THE PAGE (0353). Since the rules moved into a dialog, the
      // only place a member can SEE that a rule is on is the status line
      // beside the button — and it reads the page's copy of the rule, not
      // this control's. Without the refresh, ticking the box and closing
      // the dialog left the page saying nothing at all, which is exactly
      // the "forgot it was running" the line exists to prevent. Found on
      // the real page, not by a unit test.
      onChanged()
    } catch { setAuto(!next); setFailed(true) }
  }

  // Keeping the rule's number in step with the field, once the field is
  // valid — otherwise the checkbox says "automatically" about a number
  // that is no longer on screen.
  const commitDays = async () => {
    if (!auto || days === null || days === savedDays) return
    try { await storageApi.setChatAutoDelete(days); onChanged() } catch { setFailed(true) }
  }

  return (
    // TWO LINES, ALWAYS (Season, 2026-10-30). The rule on the first —
    // "delete what is older than N days" — and its two actions on the
    // second, together. With one wrapping row the break fell wherever the
    // label ran out of room, which differs by language and by rule, so
    // the button and the checkbox sat side by side in one group and on
    // separate lines in the other. `MyFileAutoDeleteControl` below has
    // the SAME shape, and the button's min width lines the two
    // checkboxes up.
    <div className="pb-2 space-y-1.5">
      <div className="flex items-center gap-2.5 flex-wrap">
        <label className="text-xs themed-text-muted" htmlFor="my-auto-delete">
          {t('settings.storage.autoDeleteLabel')}
        </label>
        <input
          id="my-auto-delete"
          type="text"
          inputMode="numeric"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => { void commitDays() }}
          className="w-16 text-xs px-2 py-1 rounded-lg themed-border border themed-bg-tertiary themed-text"
        />
        <span className="text-xs themed-text-muted">{t('settings.storage.cleanupUnit_days')}</span>
      </div>
      <div className="flex items-start gap-2.5">
        <button
          type="button"
          onClick={() => { void previewAndDelete() }}
          disabled={busy || days === null || caught.length === 0}
          className="shrink-0 whitespace-nowrap min-w-[5.5rem] text-xs px-2.5 py-1 rounded-lg themed-border border themed-text disabled:opacity-50"
        >
          {busy
            ? t('settings.storage.cleanupWorking')
            : t('settings.storage.autoDeletePreview', { count: caught.length })}
        </button>
        <label className="flex items-start gap-2 text-xs themed-text min-w-0 pt-1">
          <input
            type="checkbox"
            checked={auto}
            disabled={days === null}
            onChange={(e) => { void toggleAuto(e.target.checked) }}
            className="mt-0.5"
          />
          <span>
            {t('settings.storage.autoDeleteKeepDoing')}
            {auto && (
              // What they just switched on, in words. A ticked box alone
              // does not say what the rule IS.
              <span className="block themed-text-muted">
                {t('settings.storage.autoDeleteActive', { days: savedDays ?? days })}
              </span>
            )}
          </span>
        </label>
        {failed && <span className="text-xs text-rose-500 pt-1">{t('settings.storage.chatStorageSaveFailed')}</span>}
      </div>
    </div>
  )
}

/**
 * The member's own rule for OLD FILES (0353) — the sibling control.
 *
 * ── Why it is beside the other one and not merged into it ─────────────
 *
 * The control above asks how long a CONVERSATION may sit idle and takes
 * the whole thing. This asks how old a FILE may be and takes the file:
 * the conversation stays, every message in it stays, and a greyed note
 * takes the file's place where its chip was.
 *
 * They are separate because the first one cannot reach the person this
 * one is for — somebody who keeps one long-running conversation and
 * comes back to it every week. It never goes idle, so nothing in it is
 * ever eligible, and the files they attached a year ago are counted
 * against their figure for ever.
 *
 * ── Same shape as its sibling, deliberately ───────────────────────────
 *
 * One number, a preview that deletes, and a checkbox that keeps doing
 * it. Two controls that look alike and are read alike, differing only in
 * the noun.
 */
function MyFileAutoDeleteControl({ sessions, savedDays, onChanged }: {
  sessions: ChatStorageSessionRow[]
  savedDays: number | null
  onChanged: () => void
}) {
  const { t } = useTranslation('core-ui')
  const confirmDialog = useConfirmOptional()
  const pendingFiles = useContext(SpacePendingFiles)
  const [text, setText] = useState(String(savedDays ?? DEFAULT_RULE_DAYS))
  const [auto, setAuto] = useState(savedDays !== null)
  // RESYNC when the saved rule changes under us — same reason as the
  // control above: the parent refetches after every delete, and without
  // this the field keeps whatever was last typed while the sentence under
  // it reads the saved value.
  const lastSaved = useRef(savedDays)
  useEffect(() => {
    if (lastSaved.current === savedDays) return
    lastSaved.current = savedDays
    setText(String(savedDays ?? DEFAULT_RULE_DAYS))
    setAuto(savedDays !== null)
  }, [savedDays])
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  /** Files the last "Delete (N)" deleted whose space the server could not
   *  release yet (D-a) — they stay listed until the recount, so say so. */
  const [spacePending, setSpacePending] = useState(0)

  const days = /^\d+$/.test(text.trim()) && Number(text.trim()) >= 1 && Number(text.trim()) <= 3650
    ? Number(text.trim())
    : null

  // The same predicate the red dot on each file row uses — see
  // `filesTakenByAgeRule`. Its one residual (a starred parent this page
  // cannot see) over-counts: the press is explicit and names a count, and
  // a dot on a file that then stays is the safe direction to be wrong in.
  const caught = days === null ? [] : filesTakenByAgeRule(sessions, days, pendingFiles)
  // What their figure would drop by — `chargedBytes`, never `size`: a
  // second reference to bytes they already uploaded is charged 0, and
  // promising the full size back is the one number this page must not
  // get wrong.
  const caughtBytes = caught.reduce((n, c) => n + c.file.chargedBytes, 0)

  const previewAndDelete = async () => {
    if (days === null || caught.length === 0) return
    const message = t('settings.storage.fileAutoDeleteConfirmBody', {
      count: caught.length, size: formatBytes(caughtBytes), days,
    })
    const ok = confirmDialog
      ? await confirmDialog({
          title: t('settings.storage.fileAutoDeleteConfirmTitle', { count: caught.length }),
          message, type: 'danger', confirmText: t('settings.storage.fileAutoDeleteConfirmYes'),
        })
      : window.confirm(message)
    if (!ok) return
    setBusy(true); setFailed(false); setSpacePending(0)
    // The bulk delete the person confirmed FINISHES as the identity that
    // confirmed it — same rule as the control above (#1316 review 2).
    const batch = pinnedBatch()
    let pending = 0
    try {
      // One at a time, through the SAME per-file delete the row's own
      // button uses. A second way in is where the two copies drift —
      // and since 0353 that route is also what leaves the note.
      for (const c of caught) {
        // Already gone (404) — released meanwhile, see `alreadyGone` — is
        // done, not a reason to stop the rest (scoped review of round 1).
        const r = await batch.run(() => storageApi.deleteChatStorageFile(c.sessionId, c.workspaceId, c.file.id))
          .catch((e: unknown) => { if (alreadyGone(e)) return null; throw e })
        if (r?.spacePending) { pending++; pendingFiles?.mark(c.file.id, true) }
      }
    } catch {
      if (!batch.ended()) setFailed(true)
    } finally {
      // IN `finally`: a loop that threw on file 4 of 9 still cleared
      // three, and calling this only on success left the member looking
      // at an error beside a list still showing files that had gone.
      onChanged()
      setBusy(false)
      // Counted up to where it stopped: a loop that threw on file 4 still
      // deleted three.
      setSpacePending(pending)
    }
  }

  const toggleAuto = async (next: boolean) => {
    if (next && days === null) return
    setAuto(next)
    setFailed(false)
    try {
      await storageApi.setChatFileAutoDelete(next ? days : null)
      // TELL THE PAGE (0353). Since the rules moved into a dialog, the
      // only place a member can SEE that a rule is on is the status line
      // beside the button — and it reads the page's copy of the rule, not
      // this control's. Without the refresh, ticking the box and closing
      // the dialog left the page saying nothing at all, which is exactly
      // the "forgot it was running" the line exists to prevent. Found on
      // the real page, not by a unit test.
      onChanged()
    } catch { setAuto(!next); setFailed(true) }
  }

  const commitDays = async () => {
    if (!auto || days === null || days === savedDays) return
    try { await storageApi.setChatFileAutoDelete(days); onChanged() } catch { setFailed(true) }
  }

  return (
    // Two lines, the same shape as `MyAutoDeleteControl` above — see the
    // note there. The two must stay identical or their checkboxes stop
    // lining up.
    <div className="pb-2 space-y-1.5">
      <div className="flex items-center gap-2.5 flex-wrap">
        <label className="text-xs themed-text-muted" htmlFor="my-file-auto-delete">
          {t('settings.storage.fileAutoDeleteLabel')}
        </label>
        <input
          id="my-file-auto-delete"
          type="text"
          inputMode="numeric"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => { void commitDays() }}
          className="w-16 text-xs px-2 py-1 rounded-lg themed-border border themed-bg-tertiary themed-text"
        />
        <span className="text-xs themed-text-muted">{t('settings.storage.cleanupUnit_days')}</span>
      </div>
      <div className="flex items-start gap-2.5">
        <button
          type="button"
          onClick={() => { void previewAndDelete() }}
          disabled={busy || days === null || caught.length === 0}
          className="shrink-0 whitespace-nowrap min-w-[5.5rem] text-xs px-2.5 py-1 rounded-lg themed-border border themed-text disabled:opacity-50"
        >
          {busy
            ? t('settings.storage.cleanupWorking')
            : t('settings.storage.fileAutoDeletePreview', { count: caught.length })}
        </button>
        <label className="flex items-start gap-2 text-xs themed-text min-w-0 pt-1">
          <input
            type="checkbox"
            checked={auto}
            disabled={days === null}
            onChange={(e) => { void toggleAuto(e.target.checked) }}
            className="mt-0.5"
          />
          <span>
            {t('settings.storage.autoDeleteKeepDoing')}
            {auto && (
              <span className="block themed-text-muted">
                {t('settings.storage.fileAutoDeleteActive', { days: savedDays ?? days })}
              </span>
            )}
          </span>
        </label>
        {failed && <span className="text-xs text-rose-500 pt-1">{t('settings.storage.chatStorageSaveFailed')}</span>}
      </div>
      {spacePending > 0 && (
        <p className="text-xs themed-text-muted">
          {t('settings.storage.fileAutoDeleteSpacePending', { count: spacePending })}
        </p>
      )}
    </div>
  )
}

/**
 * The member's two age rules, behind ONE button (Season, 2026-10-30).
 *
 * ── Why they moved off the page ───────────────────────────────────────
 *
 * Two rows of label · days · Delete · Run automatically sat between the
 * figure and the conversation list, and read as clutter on a page whose
 * job is to show what the member is holding. They are things a member
 * does occasionally, so they open on demand. The design mock had the
 * conversation rule inline; this is a deliberate departure from it.
 *
 * ── But a rule that is ON must stay visible ───────────────────────────
 *
 * Tucking a standing rule into a dialog is how somebody forgets it is
 * running and is surprised, months later, by what it took — the same
 * worry that gave a manually deleted file its note. So whenever either
 * rule is on, the page says so beside the button, in words, with its
 * number of days. The dialog is where you CHANGE a rule; the page is
 * where you can always SEE one.
 *
 * The two controls inside are the same components as before, unchanged:
 * their preview, confirmation and batch delete do not care where they
 * are drawn.
 */
function MyCleanupRulesButton({ autoDeleteDays, fileAutoDeleteDays, onOpen }: {
  autoDeleteDays: number | null
  fileAutoDeleteDays: number | null
  onOpen: () => void
}) {
  const { t } = useTranslation('core-ui')
  const status = autoDeleteDays !== null && fileAutoDeleteDays !== null
    ? t('settings.storage.cleanupRulesStatusBoth', { conv: autoDeleteDays, file: fileAutoDeleteDays })
    : autoDeleteDays !== null
      ? t('settings.storage.cleanupRulesStatusConv', { conv: autoDeleteDays })
      : fileAutoDeleteDays !== null
        ? t('settings.storage.cleanupRulesStatusFile', { file: fileAutoDeleteDays })
        : null

  return (
    <div className="flex items-center gap-2.5 flex-wrap pb-2">
      <button
        type="button"
        onClick={onOpen}
        aria-haspopup="dialog"
        className="text-xs px-2.5 py-1 rounded-lg themed-border border themed-text"
      >
        {t('settings.storage.cleanupRulesButton')}
      </button>
      {status && <span className="text-xs themed-text-muted">{status}</span>}
    </div>
  )
}

/**
 * The dialog itself — rendered at the PAGE level, not inside the list.
 *
 * ⚠️ IT MUST NOT LIVE INSIDE `MyConversationList`. Every change these
 * controls make refreshes the page, and the page answers a refresh by
 * swapping the WHOLE list for a loading skeleton and back — which
 * unmounts anything inside it. With the dialog in the list, ticking Run
 * automatically closed the dialog under the member's cursor (measured on
 * the real page: the Close button was detached mid-click). Out here it
 * survives the refresh, and so do the two controls' own fields.
 */
function MyCleanupRulesDialog({ sessions, autoDeleteDays, fileAutoDeleteDays, onChanged, onClose }: {
  sessions: ChatStorageSessionRow[]
  autoDeleteDays: number | null
  fileAutoDeleteDays: number | null
  onChanged: () => void
  onClose: () => void
}) {
  const { t } = useTranslation('core-ui')

  // ESCAPE CLOSES IT. Mounted only while open, so the listener never
  // outlives the dialog and never swallows the key for the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cleanup-rules-title"
        className="relative z-10 max-w-xl w-full max-h-[85vh] flex flex-col ws-glass-modal ws-anim-modal-in"
      >
        <div className="px-6 pt-5 pb-3.5 themed-border border-b">
          <h3 id="cleanup-rules-title" className="text-[17px] font-semibold themed-text">
            {t('settings.storage.cleanupRulesTitle')}
          </h3>
          <p className="text-xs themed-text-muted mt-1">{t('settings.storage.cleanupRulesSubtitle')}</p>
        </div>
        <div className="overflow-y-auto min-h-0 flex-1 px-6 py-4 space-y-3">
          <MyAutoDeleteControl sessions={sessions} savedDays={autoDeleteDays} onChanged={onChanged} />
          <div className="themed-border border-t" />
          <MyFileAutoDeleteControl sessions={sessions} savedDays={fileAutoDeleteDays} onChanged={onChanged} />
        </div>
        <div className="px-6 py-3.5 themed-border border-t flex justify-end">
          <button type="button" onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg themed-border border themed-text">
            {t('common:close')}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Rebuild the one cleanup answer from an OLDER server's two fields — the
 * rolling-deploy shape, where a new page meets an API instance that has not
 * restarted. The page then draws from a single path either way.
 *
 * The `idleDays` arm is the same defensive fallback the server keeps for a
 * booking with no frozen list; the colleague rows come undated, because that
 * server never sent a date for them.
 */
export function legacyCleanupView(
  sessions: ChatStorageSessionRow[],
  scheduled: { runAt: string; idleDays: number; includeStarred: boolean; caughtSessionIds?: string[] | null } | null,
  caughtByOtherCleanup: string[] | undefined,
): MemberCleanupView {
  const bookings: CleanupBooking[] = []
  const takes: CleanupTake[] = []
  if (scheduled) {
    bookings.push({ key: 'own', own: true, kind: 'member', runAt: scheduled.runAt, includeStarred: scheduled.includeStarred })
    const frozen = scheduled.caughtSessionIds ? new Set(scheduled.caughtSessionIds) : null
    for (const s of sessions) {
      const on = frozen
        ? frozen.has(s.sessionId)
        : s.updatedAt > 0 && Date.now() - s.updatedAt > scheduled.idleDays * 24 * 60 * 60 * 1000
      if (on) takes.push({ booking: 'own', sessionId: s.sessionId, kept: s.starred && !scheduled.includeStarred })
    }
  }
  if (caughtByOtherCleanup?.length) {
    bookings.push({ key: 'legacy', own: false, runAt: null, includeStarred: false })
    for (const id of caughtByOtherCleanup) takes.push({ booking: 'legacy', sessionId: id, kept: false })
  }
  return { bookings, takes, unchecked: 0, rows: [] }
}

function MyConversationList({ sessions, cleanup, autoDeleteDays, fileAutoDeleteDays, openIn, onChanged, onOpenRules }: {
  sessions: ChatStorageSessionRow[]
  /**
   * What every live booking takes of hers, decided by the SERVER (Season
   * 2026-10-22). Nothing below decides which conversation goes, when, or
   * whether a star keeps it — the first cut did, in four places, and three
   * review rounds in a row found a case that reached some of them and not
   * the others.
   */
  cleanup: MemberCleanupView
  autoDeleteDays: number | null
  /** The member's rule for OLD FILES (0353). A separate number from
   *  `autoDeleteDays` — different object, different question. */
  fileAutoDeleteDays: number | null
  /** Open the two rules' dialog. The dialog itself lives at the page
   *  level — see `MyCleanupRulesDialog` for why it cannot live here. */
  onOpenRules: () => void
  /** Where things on these rows open — see `OpenIn`. */
  openIn: OpenIn
  onChanged: () => void
}) {
  const { t } = useTranslation('core-ui')
  const pendingFiles = useContext(SpacePendingFiles)
  const [expanded, setExpanded] = useRemembered('showAll')
  const [scheduledExpanded, setScheduledExpanded] = useRemembered('showAllScheduled')
  // ⚠️ HER VIEW FOLLOWS THE CLOCK TOO (scoped review of round 8, #1).
  // The server answered `running` when the page loaded; a booking whose
  // time comes while she has it open is under way from then (option A),
  // as the admin's row already turns. Re-render at the soonest runAt and
  // read each booking as of now.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const at = nextFlipAt(cleanup.bookings.filter((b) => !b.running && b.runAt).map((b) => b.runAt as string), nowMs)
    if (at === null) return
    const id = setTimeout(() => setNowMs(Date.now()), Math.min(at - Date.now() + 250, 2 ** 31 - 1))
    return () => clearTimeout(id)
  }, [cleanup.bookings, nowMs])
  const bookings = cleanup.bookings.map((b) =>
    !b.running && b.runAt && Date.parse(b.runAt) <= nowMs ? { ...b, running: true } : b)
  const [sort, setSort] = useState<'largest' | 'oldest' | 'newest'>('largest')
  const scheduledRef = useRef<HTMLDivElement>(null)

  /** Branches hang under their parent, not beside it. A branch whose
   *  parent is not in this list (the parent holds none of this member's
   *  files) is shown at the top level rather than dropped.
   *
   *  ⚠️ EVERY ROW IS RENDERED SOMEWHERE — that is the rule this page
   *  exists for. A member cannot delete what the list does not show, and
   *  the bytes keep counting either way, so "dropped as malformed" is
   *  the one outcome worse than a strange-looking row. Rows that form a
   *  parent LOOP (a row that is its own parent; A under B under A) have
   *  no honest place in a tree, so they are shown at the top level
   *  instead, which is also what makes the recursion below terminate. */
  const byId = new Map(sessions.map((s) => [s.sessionId, s]))
  const loops = (s: ChatStorageSessionRow): boolean => {
    const seen = new Set<string>([s.sessionId])
    let p = s.parentId
    while (p && byId.has(p)) {
      if (seen.has(p)) return true
      seen.add(p)
      p = byId.get(p)!.parentId
    }
    return false
  }
  const rootIds = new Set(sessions.filter((s) => !s.parentId || !byId.has(s.parentId) || loops(s)).map((s) => s.sessionId))
  const roots = sessions.filter((s) => rootIds.has(s.sessionId))
  // A row promoted to the top level does not ALSO hang under its parent —
  // without that the loop members would render each other forever.
  const branchesOf = (id: string) =>
    sessions.filter((s) => s.parentId === id && !rootIds.has(s.sessionId))

  const sorted = [...roots].sort((a, b) => {
    if (sort === 'largest') return b.bytes - a.bytes
    if (sort === 'oldest') return a.updatedAt - b.updatedAt
    return b.updatedAt - a.updatedAt
  })

  // ─── Reading the server's answer — indexes only, no rules ────────────
  const bookingByKey = new Map(bookings.map((b) => [b.key, b]))
  /** Every row a take may name: her rows, and the ones holding none of her
   *  files (cost nothing, so never in the storage list). */
  const rowById = new Map<string, ChatStorageSessionRow>(sessions.map((s) => [s.sessionId, s]))
  for (const r of cleanup.rows) {
    if (!rowById.has(r.sessionId)) {
      rowById.set(r.sessionId, {
        sessionId: r.sessionId, workspaceId: r.workspaceId, title: r.title,
        updatedAt: r.updatedAt, starred: r.starred, parentId: r.parentId,
        bytes: 0, files: [], fileCount: 0,
      })
    }
  }
  const goingBy = new Map<string, CleanupBooking[]>()
  const keptByOwn = new Set<string>()
  for (const take of cleanup.takes) {
    const b = bookingByKey.get(take.booking)
    if (!b || !rowById.has(take.sessionId)) continue
    if (take.kept) keptByOwn.add(take.sessionId)
    else goingBy.set(take.sessionId, [...(goingBy.get(take.sessionId) ?? []), b])
  }
  const firstRunAtById = new Map<string, string | null>()
  for (const [id, bs] of goingBy) {
    firstRunAtById.set(id, bs.filter((b) => !b.running).map((b) => b.runAt).filter((r): r is string => !!r).sort()[0] ?? null)
  }
  /** Being deleted right now — a running booking takes it. */
  const goingNow = (s: ChatStorageSessionRow): boolean => (goingBy.get(s.sessionId) ?? []).some((b) => b.running)

  const caughtBy = (s: ChatStorageSessionRow): boolean => goingBy.has(s.sessionId)
  /**
   * What the member's OWN rules take within the next `RULE_WARN_DAYS`,
   * when they are ON, and when (Season, 2026-10-30: once a rule is
   * ticked, the page has to show what it is about to delete — from three
   * days ahead, saying how long is left).
   *
   * From the SAVED rule, never the dialog's field: a number typed and not
   * yet saved deletes nothing, so it must not mark anything either.
   *
   * The SAME predicates as the dialog's "Delete (N)", run with the
   * threshold brought forward — so a dot can never mark something the
   * rule would not take, only take it earlier than the rule does.
   */
  const sessionById = new Map(sessions.map((x) => [x.sessionId, x]))
  const ruleConvDue = new Map(autoDeleteDays !== null
    ? convsTakenByIdleRule(sessions, autoDeleteDays - RULE_WARN_DAYS)
        .map((x) => [x.sessionId, convRuleDueAt(x, sessionById, autoDeleteDays)] as const)
    : [])
  const ruleFileDue = new Map(fileAutoDeleteDays !== null
    ? filesTakenByAgeRule(sessions, fileAutoDeleteDays - RULE_WARN_DAYS, pendingFiles)
        .map((c) => [c.file.id, (c.file.uploadedAt as number) + fileAutoDeleteDays * DAY_MS] as const)
    : [])
  const ruleConvDueAt = (s: ChatStorageSessionRow): number | null => ruleConvDue.get(s.sessionId) ?? null
  const ruleFileDueAt = (id: string): number | null => ruleFileDue.get(id) ?? null
  /** Kept by her star — and taken by nothing else. */
  const keptByStar = (s: ChatStorageSessionRow): boolean => keptByOwn.has(s.sessionId) && !caughtBy(s)
  const firstRunAt = (s: ChatStorageSessionRow): string | null => firstRunAtById.get(s.sessionId) ?? null
  /** The dot's hover text: the day it goes (Season 2026-10-22), and on a
   *  starred row why the star is not saving it. */
  const dotTitleOf = (s: ChatStorageSessionRow): string | undefined => {
    const when = firstRunAt(s)
    const day = goingNow(s) ? t('settings.storage.cleanupGoingNow') : when ? formatDay(when) : null
    const why = s.starred ? t('settings.storage.cleanupGoesBadgeStarred') : null
    if (day && why) return `${day} · ${why}`
    return day ?? why ?? undefined
  }
  /** STARRING is locked when ANY booking that takes it ignores stars.
   *  Taking a star off is never locked — the star also governs her own
   *  auto-delete rule. */
  const starLocked = (s: ChatStorageSessionRow): boolean =>
    !s.starred && (goingBy.get(s.sessionId) ?? []).some((b) => b.includeStarred)

  const rowsOf = (key: string, kept: boolean) => {
    const ids = new Set(cleanup.takes.filter((x) => x.booking === key && x.kept === kept).map((x) => x.sessionId))
    return [...ids].map((id) => rowById.get(id)).filter((s): s is ChatStorageSessionRow => !!s)
  }
  const own = bookings.find((b) => b.own)
  /** Her booking's title and countdown, worked out ONCE for both of its
   *  banners: running says so; otherwise its date. */
  const ownTitle = !own ? '' : own.running
    ? t('settings.storage.cleanupBannerRunningTitle')
    : t('settings.storage.cleanupBannerTitle', { when: own.runAt ? formatDay(own.runAt) : '' })
  const ownRunAt = own && !own.running ? own.runAt : null
  const ownGoing = own ? rowsOf(own.key, false) : []
  const ownKept = own ? rowsOf(own.key, true).filter(keptByStar) : []
  // Dated bookings only: an older server's undated colleague rows never had
  // a banner and still do not — their dots carry the warning.
  const otherBookings = bookings
    .filter((b) => !b.own && b.runAt)
    .sort((a, b) => (a.runAt ?? '').localeCompare(b.runAt ?? ''))

  /** Soonest first; undated after the dated ones; star-kept rows last. */
  const going = [...rowById.values()].filter(caughtBy).sort((a, b) => {
    // Being deleted now comes before anything merely scheduled.
    if (goingNow(a) !== goingNow(b)) return goingNow(a) ? -1 : 1
    const x = firstRunAt(a)
    const y = firstRunAt(b)
    if (x === y) return b.bytes - a.bytes
    if (!x) return 1
    if (!y) return -1
    return x.localeCompare(y)
  })
  const scheduledRows = [...going, ...ownKept]
  const scheduledShown = scheduledExpanded ? scheduledRows : scheduledRows.slice(0, MY_INITIAL_ROWS)
  const scheduledHidden = scheduledRows.slice(scheduledShown.length)
  /** Opens the list as well as scrolling to it — collapsed after a handful,
   *  the rows a banner is about could sit behind "N more" (review round
   *  3, finding 6). Absent when there is no list to go to. */
  const seeWhich = scheduledRows.length > 0
    ? () => { setScheduledExpanded(true); scheduledRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
    : undefined

  // Over ALL the member's conversations, not the page's slice: the other
  // copy of a shared file is just as likely to be below the fold.
  const freedBytes = useCallback(
    (row: ChatStorageSessionRow) => freedByDeleting(row, sessions),
    [sessions],
  )
  const shown = expanded ? sorted : sorted.slice(0, MY_INITIAL_ROWS)
  const hidden = sorted.slice(shown.length)
  /**
   * Whether anything behind "N more" is going — a conversation a booking
   * or her own rule takes, or a file her own rule takes, in a hidden row
   * OR in any branch under one (branches hang under their parent, so they
   * are hidden with it). One red dot for all of it, with the details on
   * the rows once expanded (Season 2026-10-30: the badge with a count and
   * a date became a dot, and it now covers her own rules too).
   */
  const goesAnywhereIn = (s: ChatStorageSessionRow, seen = new Set<string>()): boolean => {
    if (seen.has(s.sessionId)) return false
    seen.add(s.sessionId)
    if (caughtBy(s) || ruleConvDue.has(s.sessionId)) return true
    if (Array.isArray(s.files) && s.files.some((f) => ruleFileDue.has(f.id))) return true
    return branchesOf(s.sessionId).some((b) => goesAnywhereIn(b, seen))
  }
  const hiddenGoes = hidden.some((s) => goesAnywhereIn(s))
  // Pulses while a booking is deleting one of them now, like a row's dot.
  const hiddenGoingNow = hidden.some(goingNow)

  return (
    /* ⚠️ A CONTAINER, NOT A VIEWPORT (0353). This list sits in the
       Settings content column, which is much narrower than the window,
       so `md:`/`lg:` are true while the row has no room — the same thing
       the drive list's own ladder exists for. The tiers are in
       `index.css` under `.chatstore-cq`. */
    <div className="chatstore-cq">
      {/* HER OWN BOOKING (Season 2026-10-22, six cases).
          1/2 something goes — the star line depends on whether this
              booking honours stars;
          3/4 only conversations holding none of her files go — no size,
              and no "move their files": there are none of hers in them;
          5   all of it is kept by her stars — quiet, nothing is at risk;
          6   nothing of hers is left on it — no banner, UNLESS some of it
              could not be looked up, in which case it says so. */}
      {own && own.runAt && (ownGoing.length > 0 || (ownKept.length === 0 && cleanup.unchecked > 0)) && (
        <CleanupBanner
          title={ownTitle}
          runAt={ownRunAt}
          rows={ownGoing}
          canStar={(s) => !!s.workspaceId && !starLocked(s)}
          honoursStars={!own.includeStarred}
          keptCount={ownKept.length}
          uncheckedCount={cleanup.unchecked}
          onSeeWhich={seeWhich}
        />
      )}
      {own && own.runAt && ownGoing.length === 0 && ownKept.length > 0 && (
        <div className="rounded-xl themed-border border px-4 py-3 mb-3">
          <div className="flex items-start gap-2.5">
            <p className="text-[13px] themed-text grow">{ownTitle}</p>
            {ownRunAt && daysUntil(ownRunAt) !== null && (
              <span className="shrink-0 text-xs themed-text-muted">{t('settings.storage.cleanupInDays', { count: daysUntil(ownRunAt)! })}</span>
            )}
          </div>
          <p className="text-xs themed-text-muted mt-0.5">
            {t('settings.storage.cleanupBannerAllKept', { count: ownKept.length })}
          </p>
          {cleanup.unchecked > 0 && (
            <p className="text-xs text-amber-500 mt-1">{t('settings.storage.cleanupBannerUnchecked', { count: cleanup.unchecked })}</p>
          )}
          {seeWhich && (
            <button type="button" onClick={seeWhich} className="mt-1.5 text-xs font-semibold text-blue-400 underline underline-offset-2">
              {t('settings.storage.cleanupBannerSeeWhich')}
            </button>
          )}
        </div>
      )}
      {/* AN UNATTRIBUTED BOOKING (legacy files with no recorded uploader)
          that takes some of her own conversations — one banner each, dated.
          Before this there was no banner at all for it: both banners were
          gated on the member having a booking of her own. */}
      {otherBookings.map((b) => {
        const rows = rowsOf(b.key, false)
        if (rows.length === 0 || !b.runAt) return null
        return (
          <CleanupBanner
            key={b.key}
            // The only bookings besides hers that can take her rows are
            // UNATTRIBUTED ones — files with no recorded uploader. Only a
            // conversation's owner can put files in it, so no colleague's
            // cleanup ever reaches her page (Season 2026-10-22).
            title={b.running
              ? t('settings.storage.cleanupBannerUnattributedRunningTitle')
              : t('settings.storage.cleanupBannerUnattributedTitle', { when: formatDay(b.runAt) })}
            runAt={b.running ? null : b.runAt}
            rows={rows}
            canStar={(s) => !!s.workspaceId && !starLocked(s)}
            honoursStars={!b.includeStarred}
            keptCount={0}
            onSeeWhich={seeWhich}
          />
        )
      })}

      <MyCleanupRulesButton
        autoDeleteDays={autoDeleteDays}
        fileAutoDeleteDays={fileAutoDeleteDays}
        onOpen={onOpenRules}
      />

      {sessions.length > 0 && (
        <>
          <div className="flex items-center gap-2.5 pb-2 flex-wrap">
            <p className="text-xs font-semibold themed-text-secondary grow">{t('settings.storage.chatStorageConversations')}</p>
            <label className="text-xs themed-text-muted" htmlFor="my-chat-sort">{t('settings.storage.sortBy')}</label>
            <select
              id="my-chat-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              className="text-xs px-2 py-1 rounded-lg themed-border border themed-bg-tertiary themed-text"
            >
              <option value="largest">{t('settings.storage.sortLargest')}</option>
              <option value="oldest">{t('settings.storage.sortOldest')}</option>
              <option value="newest">{t('settings.storage.sortNewest')}</option>
            </select>
          </div>

          {/* The rule a star actually follows, beside the stars. */}
          <p className="text-[11px] themed-text-muted pb-2 leading-relaxed">
            &#9733; {t('settings.storage.starRuleHint')}
          </p>

          {shown.map((s) => (
            <MySessionRow
              key={s.sessionId}
              s={s}
              branches={branchesOf(s.sessionId)}
              goesOnCleanup={caughtBy(s)}
              branchesOf={branchesOf}
              caughtBy={caughtBy}
              dotTitleOf={dotTitleOf}
              freedBytes={freedBytes}
              ruleConvDueAt={ruleConvDueAt}
              ruleFileDueAt={ruleFileDueAt}
              openIn={openIn}
              onChanged={onChanged}
            />
          ))}

          {hidden.length > 0 && (
            <div className="flex items-center gap-2.5 pt-1.5">
              <span className="text-xs themed-text-muted grow">
                {t('settings.storage.moreConversations', { count: hidden.length })}
              </span>
              {/* What the tail is FOR: whether any of it is about to go. */}
              {hiddenGoes && (
                <StatusDot
                  tone="danger"
                  bare
                  active={hiddenGoingNow}
                  label={t('settings.storage.cleanupGoesBadge')}
                  title={t('settings.storage.moreConversationsHaveDeletions')}
                />
              )}
              <span className="shrink-0 text-xs themed-text tabular-nums">
                {formatBytes(hidden.reduce((n, s) => n + s.bytes, 0))}
              </span>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="shrink-0 text-xs px-2.5 py-1 rounded-lg themed-border border themed-text"
              >
                {t('settings.storage.expand')}
              </button>
            </div>
          )}
        </>
      )}

      {/* EVERYTHING A SCHEDULED CLEANUP WILL TAKE (Season 2026-10-22) —
          including conversations with none of her files, which the list
          above cannot show. Flat: a branch here is listed on its own
          verdict, not under a parent that may not be going. */}
      {scheduledRows.length > 0 && (
        <div ref={scheduledRef} className="pt-5">
          <p className="text-xs font-semibold themed-text-secondary pb-1">{t('settings.storage.chatStorageScheduledHeading')}</p>
          <p className="text-[11px] themed-text-muted pb-2 leading-relaxed">{t('settings.storage.chatStorageScheduledHint')}</p>
          {scheduledShown.map((s) => (
            <MySessionRow
              key={s.sessionId}
              s={s}
              branches={[]}
              goesOnCleanup={caughtBy(s)}
              branchesOf={() => []}
              caughtBy={caughtBy}
              dotTitleOf={dotTitleOf}
              freedBytes={freedBytes}
              ruleConvDueAt={ruleConvDueAt}
              ruleFileDueAt={ruleFileDueAt}
              starLocked={starLocked(s)}
              kept={keptByStar(s)}
              openIn={openIn}
              onChanged={onChanged}
              listName="scheduled"
            />
          ))}
          {scheduledHidden.length > 0 && (
            <div className="flex items-center gap-2.5 pt-1.5">
              <span className="text-xs themed-text-muted grow">
                {t('settings.storage.moreConversations', { count: scheduledHidden.length })}
              </span>
              <button
                type="button"
                onClick={() => setScheduledExpanded(true)}
                className="shrink-0 text-xs px-2.5 py-1 rounded-lg themed-border border themed-text"
              >
                {t('settings.storage.expand')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * One booking, as the member is told about it.
 *
 * Two separate questions, kept apart (review round 3, finding 9): does the
 * booking HONOUR stars, and can she star ANY of its rows from here. The
 * "kept because you starred it" line answers the first; the "star the ones
 * you need" advice needs both. No size when none of HER files are in it — it
 * frees none of her quota, and there are no files of hers to move.
 */
function CleanupBanner({ title, runAt, rows, canStar, honoursStars, keptCount, uncheckedCount = 0, onSeeWhich }: {
  title: string
  /** `null` while it is running: there is no date ahead to count down to. */
  runAt: string | null
  rows: ChatStorageSessionRow[]
  /** Whether a star could keep THIS row from here: it is hers (a colleague's
   *  thread has no star for her) and no booking taking it ignores stars —
   *  the row's own greyed star and this advice must not disagree (review
   *  round 4, finding 5). */
  canStar: (s: ChatStorageSessionRow) => boolean
  honoursStars: boolean
  /** Hers, kept off this booking by a star. Said first when there are any. */
  keptCount: number
  /** On this booking but not looked up on this request — their workspace
   *  would not open. The run opens it itself, days later. */
  uncheckedCount?: number
  /** Absent when there is no scheduled list to scroll to. */
  onSeeWhich?: () => void
}) {
  const { t } = useTranslation('core-ui')
  const bytes = rows.reduce((n, s) => n + s.bytes, 0)
  const starred = rows.filter((s) => s.starred).length
  // Advice to star only when she can follow it on at least one row.
  // ⚠️ NOT WHILE IT RUNS (`runAt === null`; full review round 3, #2). Stars
  // are read when each pass is planned, so a star now may be too late —
  // what her start-now bell says. The neutral body, not "star the ones you
  // need".
  const adviseStar = honoursStars && runAt !== null && rows.some(canStar)
  const key = adviseStar
    ? (bytes > 0 ? 'settings.storage.cleanupBannerBody' : 'settings.storage.cleanupBannerBodyNoSize')
    : (bytes > 0 ? 'settings.storage.cleanupBannerBodyNoStars' : 'settings.storage.cleanupBannerBodyNoStarsNoSize')
  return (
    <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 mb-3 space-y-2">
      <div className="flex items-start gap-2.5">
        <span className="text-rose-400 shrink-0">&#9888;</span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold themed-text">{title}</p>
          {rows.length > 0 && (
            <p className="text-[13px] themed-text mt-0.5 leading-relaxed">
              {honoursStars && keptCount > 0 && (
                <><span>{t('settings.storage.cleanupBannerKept', { count: keptCount })}</span>{' '}</>
              )}
              {t(key, { count: rows.length, size: formatBytes(bytes) })}
            </p>
          )}
          {uncheckedCount > 0 && (
            <p className="text-[13px] text-amber-500 mt-1 leading-relaxed">
              {t('settings.storage.cleanupBannerUnchecked', { count: uncheckedCount })}
            </p>
          )}
          {/* STARS DO NOT SAVE THEM FROM THIS ONE. Silence here would have
              somebody starring all week and losing the work anyway. */}
          {!honoursStars && starred > 0 && (
            <p className="text-[13px] text-rose-300 leading-relaxed pt-2 mt-2 themed-border border-t">
              {t('settings.storage.cleanupBannerStarred', { count: starred })}
            </p>
          )}
          {onSeeWhich && (
            <button type="button" onClick={onSeeWhich} className="mt-1.5 text-xs font-semibold text-blue-400 underline underline-offset-2">
              {t('settings.storage.cleanupBannerSeeWhich')}
            </button>
          )}
        </div>
        {runAt && daysUntil(runAt) !== null && (
          <span className="shrink-0 text-xs themed-text-muted">{t('settings.storage.cleanupInDays', { count: daysUntil(runAt)! })}</span>
        )}
      </div>
    </div>
  )
}

/** "16 Oct" — the day, which is what the member acts on.
 *
 *  ⚠️ NO YEAR, on purpose: every date this renders is a booked cleanup,
 *  which is days away. It is NOT the one for stored dates — an upload or
 *  a last-used time is routinely a year old, and those go through
 *  `formatDateWithYear` (`../budget/bits`), which keeps the year. */
function formatDay(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** "in 7 days" — the distance, which is what tells them how much time
 *  they have. Rounded UP, so "in 1 day" never means "in two hours". */
/** Whole days until `iso`, or `null` when it is not ahead. The words are
 *  the caller's, through `t` — this used to return English "in 3 days" on
 *  every locale's page. */
function daysUntil(iso: string): number | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const days = Math.ceil((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
  return days > 0 ? days : null
}

/**
 * Members waiting on an answer, above the list (#758, Season 2026-10-15 —
 * not in the agreed artboards; added to them after).
 *
 * A notification is read once and then gone. An admin who dismissed the
 * bell, or who opens this page the next day, would otherwise have no way
 * of knowing anyone had asked — and the member's only remaining signal is
 * silence, which they cannot tell from a refusal.
 *
 * Shown only while something is pending: a permanently-present empty block
 * is a thing to scroll past, and this one is meant to be noticed.
 */
function PendingRequests({
  tenantId, requests, members, onAnswered, onAdjust,
}: {
  tenantId: string
  requests: ChatStoragePendingRequest[]
  /** The list rendered directly below this card. The figures come from
   *  HERE when the asker is in it, so the same person is not described
   *  two different ways one line apart (full review 3C, finding 5): the
   *  server's `usedLabel`/`limitLabel` are rounded to whole MB by
   *  `formatBytesForNotification` (they are written for a notification),
   *  so a member at 300 KB against a 0 B limit read "0 MB of 0 MB" here
   *  and "300.0 KB of 0 B" below. The labels stay as the fallback for an
   *  asker who is no longer a member. */
  members: MemberChatStorageRow[]
  onAnswered: () => void
  /** Absent for a read-only caller (D13) — both answers to a request are
   *  write operations, so neither button is offered. */
  onAdjust?: (email: string) => void
}) {
  const { t } = useTranslation('core-ui')
  // ONE clock for the whole list: calling `new Date()` per row could put
  // two requests made in the same second on different sides of midnight.
  const now = useMemo(() => new Date(), [])
  const waitingSince = useCallback((at?: string) => formatWaitingSince(at, now), [now])
  const [busy, setBusy] = useState<string | null>(null)
  const [declined, setDeclined] = useState<Record<string, boolean>>({})
  const [declineFailed, setDeclineFailed] = useState<Record<string, boolean>>({})

  if (requests.length === 0) return null

  const decline = async (email: string) => {
    setBusy(email)
    setDeclineFailed((m) => ({ ...m, [email]: false }))
    try {
      const r = await storageApi.declineChatStorageRequest(tenantId, email)
      // Only claim a refusal that was actually sent. Another admin can
      // answer between this list rendering and the click, and the route
      // then refuses to send a second, contradicting reply — saying
      // "Declined" here would be the same false claim the bell's handler
      // was fixed for (scoped review of full review 2, finding 4).
      if (!r.alreadyAnswered) setDeclined((m) => ({ ...m, [email]: true }))
      onAnswered()
    } catch {
      // CAUGHT, or the button just springs back and the admin is told
      // nothing (full review 3B, finding 2). There was no catch here at
      // all, so a rejected call left an unhandled promise and a silent
      // screen — which is exactly what a departed member's request did
      // before the route stopped 404ing on one.
      setDeclineFailed((m) => ({ ...m, [email]: true }))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mb-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
      <div className="text-[13px] font-semibold themed-text mb-2">
        {t('settings.storage.pendingTitle', { count: requests.length })}
      </div>
      {requests.map((r) => (
        <div key={r.email} className="flex items-center gap-2.5 py-1.5 min-w-0">
          <span className="min-w-0 flex-1">
            {/* Derived HERE, not taken from the server's `r.name`, so this
                row spells the person exactly as the member list below it
                and the Adjust dialog it opens do. The server sends the
                email's local part; three surfaces on one card used to show
                three spellings (full review 2, finding 12). */}
            {/* Season 2026-10-22: the stamp goes on the NAME line, not the
                one below. That line is short and will not be truncated;
                the second line carries the usage and the member's note,
                and a long note would push the time out of sight — which
                is exactly the information the admin opened this for. */}
            {/* ⚠️ THE NAME TRUNCATES, THE STAMP DOES NOT. Both inside one
                `truncate` span put the stamp last, so a long local part
                (`firstname.lastname.operations`) ate it — the exact
                failure moving it off the second line was meant to avoid,
                and silently, for precisely the longest names (review
                finding 4). */}
            <span className="flex items-baseline gap-1 text-[13px] themed-text min-w-0">
              <span className="truncate">{nameFromEmail(r.email)}</span>
              {waitingSince(r.askedAt) && (
                <span className="themed-text-muted font-normal shrink-0 whitespace-nowrap">· {waitingSince(r.askedAt)}</span>
              )}
            </span>
            <span className="block text-[11.5px] themed-text-muted truncate">
              {(() => {
                const m = members.find((x) => x.email === r.email)
                const figure = m
                  ? (m.limitBytes === null
                      ? `${formatBytes(m.totalBytes)} · ${t('settings.storage.chatStorageUnlimited')}`
                      // `chatStorageOf`, not a literal " of " — the same phrase
                      // MemberRow and MyFigure render through the key, two files
                      // away, in all four locales (full review 2, finding 10).
                      : t('settings.storage.chatStorageOf', { used: formatBytes(m.totalBytes), limit: formatBytes(m.limitBytes) }))
                  : r.usedLabel && r.limitLabel
                    ? t('settings.storage.chatStorageOf', { used: r.usedLabel, limit: r.limitLabel })
                    : null
                return [figure, r.note ? `\u201C${r.note}\u201D` : null].filter(Boolean).join(' · ')
              })()}
            </span>
          </span>
          {!onAdjust ? null : declined[r.email] ? (
            <span className="shrink-0 text-[11.5px] themed-text-muted">{t('settings.storage.pendingDeclined')}</span>
          ) : (
            <>
              {/* BESIDE the buttons, never instead of them (scoped review
                  of full review 3, orgs finding 2). The first cut replaced
                  the pair, and nothing cleared the flag or refetched — so
                  one dropped request left the admin reading "Could not
                  send that reply" with no retry and no Adjust limit until
                  they reloaded the whole page. Fixing a silent failure by
                  removing the way to try again is not a fix. */}
              {declineFailed[r.email] && (
                <span className="shrink-0 text-[11.5px] text-red-500">{t('settings.storage.pendingDeclineFailed')}</span>
              )}
              {/* DISABLED for a departed asker, rather than silently
                  doing nothing. Removing someone deletes THEIR
                  notification rows, not the admins' copies of their ask,
                  so the amber row survives — and `onAdjust` resolves the
                  member out of the list and quietly returns when absent.
                  The admin clicked it two or three times before trying
                  Decline, which the route was deliberately made to
                  accept for exactly this case (full review 4B, finding
                  4). */}
              <button
                type="button"
                disabled={!members.some((m) => m.email === r.email)}
                title={members.some((m) => m.email === r.email) ? undefined : t('settings.storage.pendingNoLongerMember')}
                onClick={() => onAdjust(r.email)}
                className="shrink-0 text-xs px-2.5 py-1 rounded-lg themed-border border themed-bg-tertiary themed-text font-semibold"
              >
                {t('settings.storage.chatStorageAdjustLimit')}
              </button>
              <button
                type="button"
                disabled={busy === r.email}
                onClick={() => decline(r.email)}
                className="shrink-0 text-xs px-2 py-1 themed-text-muted hover:themed-text disabled:opacity-60"
              >
                {t('settings.storage.pendingDecline')}
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  )
}

export function ChatStorageSection({ tenantId, isAdmin: isAdminProp, canManage: canManageProp = true, personal = false, onLimitsChanged, quotaBytes, promisedBytes }: {
  tenantId: string
  /** May SEE the organisation view at all (`manage_chat_storage: read`). */
  isAdmin: boolean
  /** May CHANGE limits (`manage_chat_storage: write`). D13 splits these:
   *  a tenant can grant someone the figures without granting them the
   *  controls, and showing a button that can only 403 is worse than not
   *  showing it. Defaults true so a caller that knows only the old single
   *  flag keeps the old behaviour. */
  canManage?: boolean
  /** Personal drive. Every personal tenant DOES carry one membership row
   *  (verified against the dev DB: 35 personal tenants, 35 rows), so the
   *  admin test passes there and the Organization view used to render —
   *  a member list of exactly one person, you, beside a Mine tab showing
   *  the same bytes again. There is no organisation here to administer. */
  personal?: boolean
  /** The organisation's ceiling, for the dialog's allocation preview. */
  quotaBytes?: number
  /** What is promised to all members today. `null` for a non-admin. */
  promisedBytes?: number | null
  /** A limit changed, so the PAGE's figures moved too — the promised total
   *  feeds the bar and the By type unused row, and both live in its
   *  response, not in this section's. */
  onLimitsChanged?: () => void
}) {
  const { t } = useTranslation('core-ui')
  // An admin starts on the organisation; a member has nothing to switch to
  // and never sees the control.
  const isAdmin = isAdminProp && !personal
  const canManage = canManageProp && isAdmin
  const [view, setView] = useState<'org' | 'mine'>('org')
  const wantsOrg = isAdmin && view === 'org'
  const [org, setOrg] = useState<ChatStorageResponse | null>(null)
  const [mine, setMine] = useState<MemberChatStorageRow | null>(null)
  const [mySessions, setMySessions] = useState<ChatStorageSessionRow[] | null>(null)
  /** A cleanup an admin booked against THIS member — the red banner, and
   *  the per-row badges, both read from it. */
  const [myScheduledCleanup, setMyScheduledCleanup] = useState<
    { runAt: string; idleDays: number; includeStarred: boolean; caughtSessionIds?: string[] | null } | null
  >(null)
  /** Conversations on ANY live booking's list — including a colleague's
   *  thread this member uploaded into, which no banner of theirs covers. */
  const [myCaughtByOtherCleanup, setMyCaughtByAnyCleanup] = useState<string[] | undefined>(undefined)
  /** The same rows per booking, dated; and her own booking's conversations
   *  that hold none of her files (Season 2026-10-22). */
  const [myCleanup, setMyCleanup] = useState<MemberCleanupView | undefined>(undefined)
  /** The member's own standing rule, so the control shows what is set. */
  const [myAutoDeleteDays, setMyAutoDeleteDays] = useState<number | null>(null)
  const [myFileAutoDeleteDays, setMyFileAutoDeleteDays] = useState<number | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [error, setError] = useState(false)
  /** The one cleanup answer — the server's, or rebuilt from an older
   *  server's two fields during a rolling deploy. */
  const myCleanupView = mySessions
    ? (myCleanup ?? legacyCleanupView(mySessions, myScheduledCleanup, myCaughtByOtherCleanup))
    : null
  /** Set when a cancel lost the race to the worker — see `cancelBooking`. */
  const [cancelRaceLost, setCancelRaceLost] = useState(false)
  /** Which page an in-flight cancel was asked on — see `cancelBooking`. */
  const pageKeyRef = useRef('')
  pageKeyRef.current = `${tenantId}:${view}`
  /**
   * ONE GUARD FOR EVERY BACKGROUND RE-READ (scoped review of round 11 —
   * a redesign: rounds 10 and 11 both patched the follow-up's guard, once
   * too tight, once too loose). `load()` bumps the generation as it
   * starts; a follow-up answer is applied only if no load started since it
   * was SENT (an answer from before her star or delete must not land over
   * them), it is still this page (tenant and view), and it is the newest
   * follow-up answer yet. Not the effect's lifetime: an effect re-run
   * (its deps changing) must not throw away an answer still worth having.
   */
  const genRef = useRef(0)
  /** A load is out: follow-ups skip their tick (scoped review of round
   *  13, #1). One sent DURING a load carries the new generation, and the
   *  load's older snapshot then landed over it; skipped, every follow-up
   *  that applies was sent after the load had landed. */
  const loadingRef = useRef(false)
  /** A last look that waited for a load (see the last-look effect). */
  const pendingLookRef = useRef(false)
  /** The tenant reset ran this commit: the rows on screen are the old
   *  tenant's until it re-renders. */
  const resetPendingRef = useRef(false)
  const followSeqRef = useRef({ sent: 0, applied: 0 })
  const followTicket = () => ({ gen: genRef.current, page: pageKeyRef.current, seq: ++followSeqRef.current.sent })
  const followCurrent = (tk: { gen: number; page: string; seq: number }) => {
    if (genRef.current !== tk.gen || pageKeyRef.current !== tk.page || tk.seq < followSeqRef.current.applied) return false
    followSeqRef.current.applied = tk.seq
    return true
  }
  /** What the last cancel or stop did — said out loud, because a cancel
   *  that landed after deletions began used to read as "nothing happened"
   *  (issues-to-file #3, ⑤). Cleared with the lost-race line. */
  const [cancelOutcome, setCancelOutcome] = useState<CancelOutcome | null>(null)
  const clearCancelNotes = () => { setCancelRaceLost(false); setCancelOutcome(null) }
  // EVERYTHING this component holds is one tenant's. Switching orgs
  // re-runs the fetches, and until they land the old rows are still in
  // state — the page is mounted unkeyed, which is the same premise its
  // own figures rest on. Without this, a failed fetch
  // on the new tenant left Org A's member emails and limits rendered
  // under Org B (scoped review round 9, finding 1).
  useEffect(() => {
    setOrg(null); setMine(null); setMySessions(null); setMyScheduledCleanup(null); setMyCaughtByAnyCleanup(undefined); setMyCleanup(undefined); setMyAutoDeleteDays(null); setMyFileAutoDeleteDays(null)
    // Another organisation's list starts folded (scoped review of round 1).
    listViewMemory.clear()
    setActivity([]); setShowingActivity(false); setRulesOpen(false)
    // The live bookings too: another organisation's map must never drive
    // this one's follow-up poll (scoped review of round 10, #1).
    setScheduled(new Map()); setUnattributedScheduled(null)
    pendingLookRef.current = false
    resetPendingRef.current = true
    // The ticks too: an "Uploader unknown" ticked in one organisation and
    // still ticked in the next would book a cleanup there nobody chose.
    setPicked(new Set()); setPickedUnattributed(false)
    setError(false)
  }, [tenantId])
  // Nothing is known until the first fetch lands. Without this the count
  // line rendered immediately as "0 members · highest use first" over an
  // empty list — on a slow link that reads as "this organisation has no
  // members", which is the one thing it never means (full review 3C,
  // finding 7). Every other card on this page has a skeleton.
  const [loading, setLoading] = useState(true)
  const [adjusting, setAdjusting] = useState<MemberChatStorageRow | null>(null)
  const [editingRoles, setEditingRoles] = useState(false)
  /** The open cleanup dialog. ONE piece of state for every entry point —
   *  they are the same action at different scopes, and separate booleans
   *  would let two open at once. */
  const [cleaning, setCleaning] = useState<
    | { kind: 'member'; member: MemberChatStorageRow }
    | { kind: 'unattributed' }
    | { kind: 'many'; members: MemberChatStorageRow[]; unattributedBytes?: number }
    | null
  >(null)
  /** Ticked members, by email. Step 1 of the design's two-step flow:
   *  choosing WHO decides nothing, which is why a mis-tick is free. */
  const [picked, setPicked] = useState<Set<string>>(new Set())
  /** The "Uploader unknown" row's tick — it has no email to go in `picked`. */
  const [pickedUnattributed, setPickedUnattributed] = useState(false)
  /** Bookings that already exist — the SCHEDULED badges and the cancel on
   *  each row. Reloaded with the list, because booking or cancelling one
   *  changes what the rows may offer. */
  const [scheduled, setScheduled] = useState<Map<string, LiveCleanup>>(new Map())
  const [unattributedScheduled, setUnattributedScheduled] = useState<LiveCleanup | null>(null)
  // ⚠️ RE-RENDER WHEN A BOOKING'S TIME COMES (option A, scoped review of
  // round 7, #2). Under way includes "due", so a page left open across a
  // `runAt` must turn that row "running" (menu: Stop, not Cancel) then —
  // it is known in advance, so no poll is needed for it.
  const [nowMs, setNowMs] = useState(() => Date.now())
  /** Recent cleanups for the Cleanup activity dialog (B2 step ③). Empty
   *  means none ever — and then the button is not shown at all. */
  const [activity, setActivity] = useState<CleanupActivityRow[]>([])
  const [showingActivity, setShowingActivity] = useState(false)
  /** Bumped by a successful save so the list refetches — the figures and the
   *  limits come from the same read, so a stale list after a change would
   *  show the old ceiling beside the new bar. */
  const [reloadKey, setReloadKey] = useState(0)
  /** The member's rules dialog, held HERE rather than in the list, so a
   *  refresh — which swaps the list for a skeleton — does not close it. */
  const [rulesOpen, setRulesOpen] = useState(false)
  const closeRules = useCallback(() => setRulesOpen(false), [])
  const reloadMine = () => { setReloadKey((k) => k + 1); onLimitsChanged?.() }
  const spacePendingFiles = useSpacePendingFilesState()
  const listViewMemory = useListViewMemoryState()

  // WHILE SOMETHING IS MOVING, THE DIALOG FOLLOWS IT (D35 step ④). A
  // stop says "finishing the batch in progress, then stopping", and the
  // server's answer is that it takes seconds — a list frozen at the moment
  // the dialog opened left STOPPING (and RUNNING's "at least N so far") on
  // screen until the page was reloaded. Only while the dialog is open and
  // a row is running or stopping; the list alone, not the whole page.
  // ⚠️ A DUE BOOKING IS MOVING TOO (e2e, ⑥). A "start now" waits as
  // `scheduled` until the worker's next sweep (up to a minute) and a small
  // list then goes straight to `done` — it is never seen `running`. Polling
  // only on running/stopping left the dialog the Started screen sends the
  // admin to saying SCHEDULED until the page was reloaded.
  /** Read the activity list now — every time the dialog opens, so it never
   *  shows what the page happened to load minutes ago. */
  const refreshActivity = () => {
    const askedOn = pageKeyRef.current
    storageApi.listChatStorageCleanupActivity()
      // Dropped if the admin has moved to another organisation meanwhile.
      .then((r) => { if (pageKeyRef.current === askedOn) setActivity(r.cleanups) })
      .catch(() => {})
  }
  useEffect(() => {
    const runAts = [
      ...[...scheduled.values()].filter((cu) => cu.state === 'scheduled').map((cu) => cu.runAt),
      ...(unattributedScheduled?.state === 'scheduled' ? [unattributedScheduled.runAt] : []),
      ...activity.filter((a) => a.state === 'scheduled').map((a) => a.runAt),
      ...(myCleanup?.bookings ?? []).filter((b) => !b.running && b.runAt).map((b) => b.runAt as string),
    ]
    const at = nextFlipAt(runAts, nowMs)
    if (at === null) return
    // Capped: setTimeout overflows past ~24.8 days (it would fire at once).
    const id = setTimeout(() => setNowMs(Date.now()), Math.min(at - Date.now() + 250, 2 ** 31 - 1))
    return () => clearTimeout(id)
  }, [scheduled, unattributedScheduled, activity, myCleanup, nowMs])
  // ⚠️ A ROW UNDER WAY IS FOLLOWED TO ITS END (full review round 3, #1).
  // The member rows are read once per load; since D35 "start now" ends in
  // the WORKER, so nothing re-read them: a finished cleanup stayed RUNNING
  // with a pulsing dot, its Stop answered "already ended", and the member
  // could not be ticked again.
  //
  // ⚠️ DELIBERATELY DUMB (Season 2026-10-22, after rounds 10–14). Each
  // earlier cut tried to tell WHEN something had ended and re-read only
  // then — and every way of telling disagreed with some other writer or
  // failure (a reload's skeleton, a dropped second stage, an end seen
  // first by the dialog, an activity read that failed). Now: while
  // anything is under way, every 10 s re-read the listing, the figures and
  // the activity, in place, through the follow-up guard. The listing and
  // the figures apply together or not at all (a listing applied alone
  // would end the follow-up with the figures still stale), and the figures
  // are read AFTER the listing; the activity is optional, as it is in
  // `load()`. Nothing to judge, nothing to miss.
  const liveRows = [...scheduled.values(), ...(unattributedScheduled ? [unattributedScheduled] : [])]
  // ⚠️ A STOP IS FOLLOWED TOO, until it has really stopped (scoped review
  // of round 16). A Stop takes the row out of the listing AT ONCE, while
  // the batch in hand is still being deleted; following only the listing
  // ended there, with figures from before that batch.
  const orgFollowing = wantsOrg && !error && (
    liveRows.some((cu) => liveUnderWay(cu, nowMs)) || activity.some((a) => a.stop === 'stopping'))
  /** One look: the listing, the figures, the activity — applied together
   *  through the follow-up guard (`followCurrent`). The listing is read
   *  first (scoped review of round 15). */
  const followLook = (forTenant: string) => {
    const tk = followTicket()
    void (async () => {
      const r = await storageApi.listChatStorageCleanups().catch(() => null)
      if (!r) return
      const [fig, recent] = await Promise.all([
        storageApi.getChatStorage(forTenant).catch(() => null),
        storageApi.listChatStorageCleanupActivity().catch(() => null),
      ])
      if (!fig || !followCurrent(tk)) return
      applyLive(r.cleanups)
      setOrg(fig)
      if (recent) setActivity(recent.cleanups)
    })()
  }
  useEffect(() => {
    if (!orgFollowing) return
    const id = setInterval(() => {
      // A tab nobody is looking at does not re-read (its next look does).
      if (loadingRef.current || document.hidden) return
      followLook(tenantId)
    }, 10_000)
    return () => clearInterval(id)
  }, [orgFollowing, tenantId])
  // ⚠️ ONE LAST LOOK WHEN FOLLOWING ENDS (scoped review of round 16). It
  // ends because SOMETHING showed the end — this follow-up, the dialog's
  // own poll, a reload — and an end is only ever shown after its
  // deletions committed (a finished run's chunks before `finish`, a
  // stopped one's batch before `markStopped`). So a look taken now
  // includes them all, whoever saw the end first. Not on a tenant or view
  // change, or a failure: that is the page going away, not the cleanup.
  //
  // ⚠️ AND NEVER WHILE A LOAD IS OUT (scoped review of round 17): a load
  // reads its figures and activity in parallel, so it could land after
  // the look with figures from before the end — and following, already
  // over, would never look again. The look waits for the load
  // (`pendingLookRef`, taken in its `finally`).
  const followedRef = useRef<{ following: boolean; page: string }>({ following: false, page: '' })
  useEffect(() => {
    const page = `${tenantId}:${view}`
    const was = followedRef.current
    // The first render after a tenant switch still holds the old tenant's
    // rows (they are cleared in an effect): never "following" then.
    followedRef.current = { following: orgFollowing && !resetPendingRef.current, page }
    resetPendingRef.current = false
    if (!(was.following && !orgFollowing && was.page === page && wantsOrg && !error)) return
    if (loadingRef.current) pendingLookRef.current = true
    else followLook(tenantId)
  }, [orgFollowing, tenantId, view, wantsOrg, error])
  // HER view the same way (scoped review of round 10, #3): while a booking
  // that takes her conversations is under way, re-read her page in place
  // every 15 s, so the banner ends and deleted rows leave when it is done.
  const myFollowing = !wantsOrg && !error && (myCleanup?.bookings ?? []).some((b) =>
    b.running || (!!b.runAt && Date.parse(b.runAt) <= nowMs))
  useEffect(() => {
    if (!myFollowing) return
    const id = setInterval(() => {
      if (loadingRef.current) return
      const tk = followTicket()
      Promise.all([storageApi.getMyChatStorage(tenantId), storageApi.getMyChatStorageSessions()])
        .then(([r, s]) => {
          if (!followCurrent(tk)) return
          // NOT her auto-delete setting: its control re-syncs on any change
          // of it, and a background answer would wipe a number she is
          // typing (scoped review of round 11, #4). Only her own actions
          // re-read it.
          setMine(r.member); setMySessions(s.sessions); setMyScheduledCleanup(s.scheduledCleanup ?? null)
          setMyCaughtByAnyCleanup(s.caughtByOtherCleanup); setMyCleanup(s.cleanup)
        })
        .catch(() => {})
    }, 15_000)
    return () => clearInterval(id)
  }, [myFollowing, tenantId])
  // The dialog's OWN rule for "running" (`activityUnderWay`): a row it
  // shows RUNNING is a row it follows (scoped review of round 7, #1).
  const activityMoving = activity.some((a) => a.stop === 'stopping' || activityUnderWay(a, nowMs))
  // …and while the PAGE says "stopping" (⑥b): that line is shown without
  // the dialog too (a Stop from the row's menu), and it used to stay
  // "stopping" until the next click, long after the stop had landed.
  const followingStop = cancelOutcome?.result === 'stopping'
  useEffect(() => {
    if ((!showingActivity && !followingStop) || !activityMoving) return
    let alive = true
    const id = setInterval(() => {
      storageApi.listChatStorageCleanupActivity()
        .then((r) => { if (alive) setActivity(r.cleanups) })
        .catch(() => {})
    }, 5000)
    return () => { alive = false; clearInterval(id) }
  }, [showingActivity, followingStop, activityMoving, tenantId])
  // The stop has landed: the line says what it now knows — STOPPED, and
  // the final (lower-bound) count — rather than "stopping" for ever.
  useEffect(() => {
    if (cancelOutcome?.result !== 'stopping' || !cancelOutcome.id) return
    const row = activity.find((a) => a.id === cancelOutcome.id)
    if (row?.stop === 'stopped') setCancelOutcome({ id: row.id, result: 'stopped', deleted: row.deletedCount })
  }, [activity, cancelOutcome])

  const pickedCount = picked.size + (pickedUnattributed ? 1 : 0)

  // CLEANUP ACTIVITY (B2 step ③; Season 2026-10-22: a button right beside
  // Clean up). Absent for an organisation that has never had a cleanup;
  // the indigo dot — the member rows' booked colour — while one is booked
  // or running. Shown WITHOUT `canManage` too: it is a read, and someone
  // granted the figures sees the list, only without Cancel. Opening it
  // clears an old lost-race line, which would otherwise sit above a fresh
  // list.
  const activityButton = activity.length > 0 && (
    <button
      type="button"
      onClick={() => { clearCancelNotes(); setShowingActivity(true); refreshActivity() }}
      className="whitespace-nowrap inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg themed-border border themed-bg-tertiary themed-text"
    >
      {activity.some((a) => a.state === 'scheduled' || a.state === 'running' || a.stop === 'stopping') && (
        <span className="w-2 h-2 rounded-full bg-indigo-400" aria-hidden="true" />
      )}
      {t('settings.storage.cleanupActivityButton')}
    </button>
  )

  // ⚠️ THE LOST-RACE LINE BELONGS TO ONE PAGE OF ONE ORGANISATION. Its only
  // other reset is at the top of `cancelBooking`, and this component is
  // deliberately mounted unkeyed across tenants — so the line survived a
  // switch to "My conversations" and back, and even a switch to ANOTHER
  // ORGANISATION, sitting above rows it has nothing to do with (round 13,
  // finding 3). NOT cleared on every load: the cancel itself bumps
  // `reloadKey`, so clearing there would erase the sentence before it was
  // read.
  useEffect(() => { clearCancelNotes() }, [tenantId, view])

  /**
   * Where "open this conversation" goes.
   *
   * ⚠️ BOTH HALVES ARE LOAD-BEARING, and the old code had neither. The
   * PREFIX decides which tenant's chat loads — `/~` is the personal space,
   * `/t/<id>` an organisation's — and the EMAIL decides which localStorage
   * key the queued tab is written under, because the chat reads it as
   * `useOpenTabs(workspaceId, orgEmail)`. A personal tenant has no org
   * identity, which is why that side passes `undefined` rather than an
   * address.
   *
   * The id test rather than the `personal` prop: it is what `App.tsx`
   * itself uses to build every other prefix, so the two cannot drift.
   */
  const openIn = useMemo(() => ({
    urlPrefix: tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`,
    orgEmail: tenantId.startsWith('p_') ? undefined : mine?.email,
    // For the FILE links, which the browser follows without any of the
    // headers the api-client adds — see `MySessionFileRow`.
    tenantId,
  }), [tenantId, mine?.email])

  /**
   * Cancel a booking from the row that carries its badge.
   *
   * A 409 means the worker already claimed it — by then the
   * conversations are going or gone, so the list is refetched to show
   * the truth rather than leaving a badge the admin believes they
   * cancelled.
   */
  const cancelBooking = async (id: string) => {
    // ⚠️ A LOST RACE HAS TO BE SAID OUT LOUD. Since D35 a cancel also
    // STOPS a running booking, so the 409 left is a booking that had
    // already ENDED by the time the click landed — and swallowing it left
    // the menu closing with no word at all (round 12, finding 7).
    clearCancelNotes()
    // ⚠️ THE ANSWER BELONGS TO THE PAGE IT WAS ASKED ON (#1280 review #2).
    // An admin who cancels in organisation A and switches to B (or to
    // Mine) before the answer arrives must not see A's "cancellation
    // failed" / "stopped, N deleted" above B's rows. Round 13 cleared the
    // line on a switch; a LATE answer then set it again.
    const askedOn = pageKeyRef.current
    const stillHere = () => pageKeyRef.current === askedOn
    try {
      const r = await storageApi.cancelChatStorageCleanup(id)
      if (r.result && stillHere()) setCancelOutcome({ id, result: r.result, deleted: r.deletedCount ?? null })
    } catch (e) {
      if (!stillHere()) return
      // Only THIS refusal gets the sentence. Any other failure is an
      // ordinary error and the refetch below still shows the truth.
      //
      // ⚠️ AND IT SAYS NO MORE THAN THE SERVER DOES. `cancel` matches
      // a booked or running row and refuses EVERY other outcome — done,
      // cancelled by a second admin, a stale id. The first
      // wording said "it is running now", which is one of five and is the
      // wrong one in the ordinary case (the booking simply finished, or
      // another admin cancelled it first), and it sent that admin looking
      // for a run that does not exist (round 13, finding 2).
      if ((e as { code?: string })?.code === 'CLEANUP_NOT_SCHEDULED') setCancelRaceLost(true)
    }
    setReloadKey((k) => k + 1)
    onLimitsChanged?.()
  }

  /** The member rows' live bookings, from the listing. */
  const applyLive = (cleanups: ScheduledCleanupRow[]) => {
    const byMember = new Map<string, LiveCleanup>()
    let unattrib: LiveCleanup | null = null
    for (const cu of cleanups) {
      // `state` carried through so the row can tell a BOOKING from
      // a run in progress — see `LiveCleanup`.
      const live = { id: cu.id, runAt: cu.runAt, state: cu.state }
      if (cu.target.kind === 'member') byMember.set(cu.target.email.toLowerCase(), live)
      else unattrib = live
    }
    setScheduled(byMember)
    setUnattributedScheduled(unattrib)
    // A member who has just been booked cannot be booked again, so
    // they leave the selection rather than sitting in it as a
    // guaranteed skip.
    setPicked((prev) => new Set([...prev].filter((e) => !byMember.has(e))))
    if (unattrib) setPickedUnattributed(false)
  }
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        if (wantsOrg) {
          const [r, booked, recent] = await Promise.all([
            storageApi.getChatStorage(tenantId),
            // The SCHEDULED badges. Its own failure must not take the list
            // down: the figures are what the admin came for, and a missing
            // badge only means the dialog's 409 catches the clash instead.
            storageApi.listChatStorageCleanups().catch(() => ({ cleanups: [] as ScheduledCleanupRow[] })),
            // The same for the Cleanup activity list: its failure hides the
            // button, never the page — it is a look back.
            storageApi.listChatStorageCleanupActivity().catch(() => ({ cleanups: [] as CleanupActivityRow[] })),
          ])
          if (!cancelled) {
            setOrg(r)
            setActivity(recent.cleanups)
            applyLive(booked.cleanups)
            setError(false)
          }
        } else {
          const [r, s] = await Promise.all([
            storageApi.getMyChatStorage(tenantId),
            // Served by the agent (titles live in its SQLite). Its own
            // failure must not take the figure down with it — the number is
            // the thing the member came for; the list is detail.
            storageApi.getMyChatStorageSessions().catch(() => ({
              sessions: [] as ChatStorageSessionRow[],
              scheduledCleanup: null,
              caughtByOtherCleanup: undefined as string[] | undefined,
              cleanup: undefined as MemberCleanupView | undefined,
              autoDeleteDays: null,
              fileAutoDeleteDays: null,
            })),
          ])
          if (!cancelled) { setMine(r.member); setMySessions(s.sessions); setMyScheduledCleanup(s.scheduledCleanup ?? null); setMyCaughtByAnyCleanup(s.caughtByOtherCleanup); setMyCleanup(s.cleanup); setMyAutoDeleteDays(s.autoDeleteDays ?? null); setMyFileAutoDeleteDays(s.fileAutoDeleteDays ?? null); setError(false) }
        }
      } catch {
        // The section hides rather than erroring, the same way the
        // largest-folders card does on a 403: a storage page that renders
        // an error block for one optional section reads as broken.
        //
        // Plain and whole, which it can be again now that this component
        // publishes nothing upward. Five review rounds of per-arm and
        // "loaded once" variants all existed to stop a hidden section
        // leaving a figure stranded on the page above it, and each one
        // produced a worse state than the last — a card holding only the
        // view toggle, "0 members" over an empty list, one org's rows
        // under another, and finally a section that hid itself with no
        // way back. Nothing to strand, nothing to special-case.
        if (!cancelled) setError(true)
      } finally {
        if (!cancelled) {
          setLoading(false); loadingRef.current = false
          if (pendingLookRef.current) { pendingLookRef.current = false; if (wantsOrg) followLook(tenantId) }
        }
      }
    }
    genRef.current++
    loadingRef.current = true
    setLoading(true)
    void load()
    return () => { cancelled = true }
  }, [tenantId, wantsOrg, reloadKey])

  if (error) return null

  const members = org?.members ?? []
  const shown = expanded ? members : members.slice(0, INITIAL_ROWS)
  /** Is any live cleanup actually RUNNING? The legend's indigo entry has to
   *  say which of the two meanings is on screen — see the legend. */
  const anyRunning = (unattributedScheduled ? liveUnderWay(unattributedScheduled, nowMs) : false)
    || [...scheduled.values()].some((cu) => liveUnderWay(cu, nowMs))

  return (
    <SpacePendingFiles.Provider value={spacePendingFiles}>
    <ListViewMemory.Provider value={listViewMemory}>
    <Section
      // Its OWN key, not the By type legend's `sessionAttachments`. They
      // read the same today but are different things — the agreed design
      // renames the legend rows to "Chat storage, used / unused" while the
      // section stays "Chat storage" — and sharing a key would make that
      // rename move both.
      // "My chat storage" exists to tell one member's figure apart from the
      // organisation's. On a personal drive there is no other figure to tell
      // it apart from, so it is just "Chat storage".
      title={wantsOrg || personal ? t('settings.storage.chatStorageTitle') : t('settings.storage.chatStorageMine_title')}
      info={`${t('settings.storage.chatStorageInfo')} ${wantsOrg ? t('settings.storage.chatStorageSharedNote') : ''}`.trim()}
    >
      <div className="themed-bg-secondary themed-border border rounded-xl px-4 py-3">
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2.5 pb-3">
            {/* WRAPS on a narrow window: with Cleanup activity beside them
                the buttons outgrew the row and ran off the screen (Season,
                2026-10-22). The buttons keep their labels on one line. */}
            <GlassToggle
              value={view}
              onChange={(v) => setView(v as 'org' | 'mine')}
              options={[
                { value: 'org', label: t('settings.storage.chatStorageOrganization') },
                { value: 'mine', label: t('settings.storage.chatStorageMine') },
              ]}
            />
            <span className="grow" />
            {/* Both are actions on the ORGANISATION's storage, so they go
                with the Organization view and disappear on Mine — they are
                not things you do to your own files.

                `canManage`, not `wantsOrg` alone (D13): the capability splits
                read from write, so someone granted the figures without the
                controls sees the list and no buttons, rather than buttons
                that can only 403. */}
            {/* ONE GROUP, so the buttons move together: on one line with
                the toggle they sit right (the spacer above); when the row is
                too narrow the whole group drops to the next line and stays
                on the RIGHT (Season, 2026-10-22) — as separate items, the
                first stayed right on line one and the rest went left on
                line two. */}
            {wantsOrg && (activityButton || canManage) && (
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2.5">
              {!canManage && activityButton}
              {canManage && (
                <>
                  <button
                    type="button"
                    onClick={() => setEditingRoles(true)}
                    className="whitespace-nowrap text-xs px-2.5 py-1 rounded-lg themed-border border themed-bg-tertiary themed-text"
                  >
                    {t('settings.storage.chatStorageDefaultsByRole')}
                  </button>
                  {activityButton}
                  {/* STEP 1 → STEP 2. The button is live only once somebody
                      is ticked: "clean up" with nobody chosen has no
                      meaning, and the design's flow is explicit that
                      ticking is what this acts on. */}
                  <button
                    type="button"
                    disabled={pickedCount === 0}
                    onClick={() => setCleaning(
                      // Only the uploader-unknown row ticked: its own dialog,
                      // the one its ⋮ opens — a "members" dialog of nobody.
                      picked.size === 0
                        ? { kind: 'unattributed' }
                        : {
                            kind: 'many',
                            members: (org?.members ?? []).filter((m) => picked.has(m.email)),
                            unattributedBytes: pickedUnattributed ? org?.unattributedBytes ?? 0 : undefined,
                          },
                    )}
                    className="whitespace-nowrap text-xs px-2.5 py-1 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
                  >
                    {pickedCount > 0
                      ? `${t('settings.storage.cleanupSelected', { count: pickedCount })} · ${t('settings.storage.chatStorageCleanUp')}`
                      : t('settings.storage.chatStorageCleanUp')}
                  </button>
                </>
              )}
              </div>
            )}
          </div>
        )}

        {loading ? (
          // A skeleton, not a count of nothing. Three bars because the
          // list is the thing being waited for.
          <div className="space-y-2.5 py-1.5" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="h-2.5 w-2.5 rounded-sm themed-bg-tertiary animate-pulse" />
                <div className="h-3 grow rounded themed-bg-tertiary animate-pulse" />
              </div>
            ))}
          </div>
        ) : wantsOrg ? (
          <>
            <PendingRequests
              tenantId={tenantId}
              requests={org?.pendingRequests ?? []}
              members={members}
              onAnswered={() => setReloadKey((k) => k + 1)}
              // Opens the member's own dialog, which is where an admin
              // answers YES — the same control the row's notification
              // points at, so the two cannot drift.
              // Read-only callers get no way in: answering a request IS a
              // limit change, and `PATCH /members/chat-storage` requires
              // write.
              onAdjust={canManage ? (email) => {
                const m = org?.members.find((x) => x.email === email)
                if (m) setAdjusting(m)
              } : undefined}
            />
            <div className="flex items-center gap-2.5 pb-2">
              <span className="w-2.5 h-2.5 rounded-sm bg-cyan-500 shrink-0" />
              <span className="text-xs themed-text-muted">
                {t('settings.storage.chatStorageMemberCount', { count: members.length })}
              </span>
              <span className="grow" />
              {canManage && (
                // The design's shortcut: the people this page exists to
                // find. It ADDS to the selection rather than replacing it,
                // so an admin who ticked somebody specific first does not
                // silently lose them.
                //
                // Members who already have a booking are left out — the
                // database refuses a second one, so including them would
                // only manufacture skips.
                <button
                  type="button"
                  onClick={() => setPicked((prev) => {
                    const out = new Set(prev)
                    for (const m of members) {
                      if (scheduled.has(m.email.toLowerCase())) continue
                      if (m.limitBytes === null || m.limitBytes === 0) continue
                      if (m.totalBytes / m.limitBytes >= 0.8) out.add(m.email)
                    }
                    return out
                  })}
                  className="text-xs px-2.5 py-1 rounded-lg themed-border border themed-bg-tertiary themed-text"
                >
                  {t('settings.storage.cleanupSelectOver80')}
                </button>
              )}
            </div>
            {canManage && (
              // The design's own words, and the sentence that keeps the
              // two steps apart in the reader's head.
              <p className="text-[11px] themed-text-muted pb-2 leading-relaxed">
                {t('settings.storage.cleanupPickHint')}
              </p>
            )}
            {/* ⚠️ OUTSIDE THE LEGEND, AND AT EVERY WIDTH. The first cut of
                this line was nested INSIDE the legend's `<p>` — which is
                `xl:hidden`, so on the 1440px monitor most admins use the
                cancel went back to being completely silent, which is the
                whole thing it was added to stop (round 13, finding 1). It
                was also a `<p>` inside a `<p>`, which is invalid HTML. The
                test asserts it is not inside that container, because jsdom
                applies no CSS and so cannot see `xl:hidden` at all. */}
            {cancelRaceLost && (
              <p data-testid="cancel-race-lost" className="text-[12px] text-amber-500 pb-2">
                {t('settings.storage.cleanupCancelRaceLost')}
              </p>
            )}
            {cancelOutcome && <CancelOutcomeLine outcome={cancelOutcome} className="pb-2" />}
            {/* THE LEGEND. The dots carry three states on a narrow screen
                where their words do not fit, and a colour nobody has been
                told the meaning of is decoration. Shown only below the
                width at which the words are on the rows themselves. */}
            <p className="xl:hidden flex items-center gap-3 flex-wrap text-[10px] themed-text-muted pb-2">
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />{t('settings.storage.cleanupOverLimitBadge')}
              </span>
              {/* ⚠️ THE INDIGO DOT MEANS TWO THINGS, and below `xl` the dot
                  is ALL the admin gets — which is why this legend exists at
                  all. A `running` row (including a run-now whose
                  conversations are already deleted) wore an indigo dot the
                  legend defined as "Scheduled for deletion", i.e. "not done
                  yet, still cancellable"; the menu then told them there was
                  nothing to cancel. Round 11 taught the badge and both menus
                  the difference and left this line behind (round 12,
                  finding 6).
                  The running entry appears only when one is actually
                  running, so the ordinary legend is unchanged. */}
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-400" />{t('settings.storage.cleanupScheduledBadge')}
              </span>
              {anyRunning && (
                <span className="inline-flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-400 animate-pulse" />{t('settings.storage.cleanupRunningBadge')}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full border-[1.5px] border-current themed-text-secondary" />{t('settings.storage.chatStorageCustomBadge')}
              </span>
            </p>
            {shown.map((m) => (
              <MemberRow
                key={m.email}
                m={m}
                onAdjust={canManage ? setAdjusting : undefined}
                onCleanUp={canManage ? (mm) => setCleaning({ kind: 'member', member: mm }) : undefined}
                selected={canManage ? picked.has(m.email) : undefined}
                onSelect={canManage ? (email, next) => setPicked((prev) => {
                  const out = new Set(prev)
                  if (next) out.add(email); else out.delete(email)
                  return out
                }) : undefined}
                scheduled={asOfNow(scheduled.get(m.email.toLowerCase()), nowMs)}
                onCancelScheduled={canManage ? (id) => { void cancelBooking(id) } : undefined}
              />
            ))}
            {/* Bytes nobody owns, shown only when there are any. Folding them
                into a member would be a lie, and dropping them would leave
                the tenant figure unexplainable. */}
            {(org?.unattributedBytes ?? 0) > 0 && (
              <UnattributedRow
                bytes={org?.unattributedBytes ?? 0}
                canManage={canManage}
                onCleanUp={() => setCleaning({ kind: 'unattributed' })}
                scheduled={asOfNow(unattributedScheduled ?? undefined, nowMs)}
                onCancelScheduled={canManage ? (id) => { void cancelBooking(id) } : undefined}
                selected={canManage ? pickedUnattributed : undefined}
                onSelect={canManage ? setPickedUnattributed : undefined}
              />
            )}
            {members.length > INITIAL_ROWS && !expanded && (
              <div className="flex pt-2">
                <span className="grow" />
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="text-xs themed-text-muted underline underline-offset-2"
                >
                  {t('settings.storage.chatStorageShowAll', { count: members.length })}
                </button>
              </div>
            )}
          </>
        ) : (
          mine && (
            <>
              {/* `canManage` suppresses "Ask for space": an admin IS the
                  person who grants it. Excluding the asker from their own
                  fan-out (correct — they used to ring themselves and could
                  Decline against themselves) means a SOLE admin asking
                  reaches nobody, and the member's page then said "no one
                  in this organisation can change storage limits" to the
                  one person who can (scoped review of the convergence
                  pass, finding 8). The useful next step is one tab away,
                  so the button simply is not offered. */}
              <MyFigure member={mine} tenantId={tenantId} canAsk={!canManage} />
              {/* The conversations behind that figure. A member asked to
                  clear space needs to know WHERE it is; a single number
                  tells them they are full and nothing else. Collapsed by
                  default (the agreed design) — it is reference, not the
                  headline. */}
              {mySessions !== null && (
                <div className="mt-2 pt-2 themed-border border-t">
                  {/* Empty only when there is nothing to SHOW — a member
                      with no uploads can still have conversations on a
                      booking, and those are the ones nothing else lists. */}
                  {mySessions.length === 0 && !myCleanupView?.rows.length && !myCleanupView?.unchecked ? (
                    <p className="text-xs themed-text-muted py-1">
                      {t('settings.storage.chatStorageNoConversations')}
                    </p>
                  ) : (
                    <MyConversationList
                      sessions={mySessions}
                      cleanup={myCleanupView!}
                      autoDeleteDays={myAutoDeleteDays}
                      fileAutoDeleteDays={myFileAutoDeleteDays}
                      openIn={openIn}
                      onChanged={reloadMine}
                      onOpenRules={() => setRulesOpen(true)}
                    />
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>

      {adjusting && (
        <AdjustChatLimitDialog
          tenantId={tenantId}
          member={adjusting}
          // What this member's ROLE resolves to, so the first option can say
          // what following it means. Read from the SAME response the list
          // came from, never recomputed here — a second copy of the
          // precedence rule is how the dialog and the server come to
          // disagree about what "the default" is.
          roleDefaultBytes={(() => {
            const d = org?.roleDefaults.find((r) => r.role === adjusting.role)
            // `0`, NOT `null`. `roleDefaults` carries the six shipped
            // roles while `memberships.role` is plain text with no
            // CHECK, so a membership on any other role finds nothing
            // here — and `null` means UNLIMITED downstream, while
            // `resolveChatStorageLimit` deliberately returns 0 (blocked)
            // for a role it does not know. The dialog promised "no limit
            // today" for someone the gate refuses outright (full review
            // 4C, finding 11). Fail the way the resolver fails.
            if (!d) return 0
            return d.isOverridden ? d.overrideBytes ?? null : d.shippedBytes
          })()}
          quotaBytes={quotaBytes}
          promisedBytes={promisedBytes ?? null}
          onClose={() => setAdjusting(null)}
          onSaved={() => { setReloadKey((k) => k + 1); onLimitsChanged?.() }}
        />
      )}
      {cleaning?.kind === 'many' && (
        <CleanUpMembersDialog
          members={cleaning.members}
          unattributedBytes={cleaning.unattributedBytes}
          onClose={() => setCleaning(null)}
          // Refetch rather than adjust in place: the figures, the member
          // rows, the badges and the unattributed row all come from one
          // read, and a list that lost conversations while the totals
          // stayed put is the contradiction this section has been bitten
          // by before.
          onBooked={() => { setPicked(new Set()); setPickedUnattributed(false); setReloadKey((k) => k + 1); onLimitsChanged?.() }}
        />
      )}
      {(cleaning?.kind === 'member' || cleaning?.kind === 'unattributed') && (
        <CleanUpChatStorageDialog
          target={cleaning.kind}
          member={cleaning.kind === 'member' ? cleaning.member : undefined}
          unattributedBytes={cleaning.kind === 'unattributed' ? org?.unattributedBytes ?? 0 : undefined}
          onClose={() => setCleaning(null)}
          onBooked={() => { setReloadKey((k) => k + 1); onLimitsChanged?.() }}
          // A cleanup that started now is watched there (D35). The list was
          // asked for again by `onBooked`; the new row shows when that lands.
          onSeeActivity={() => { setCleaning(null); clearCancelNotes(); setShowingActivity(true); refreshActivity() }}
          // "Stop it" on the started screen (D35 step ④): stop, then show
          // where it stands — the row will read STOPPING, then STOPPED.
          onStop={(id) => { setCleaning(null); setShowingActivity(true); void cancelBooking(id) }}
        />
      )}
      {rulesOpen && mySessions && (
        <MyCleanupRulesDialog
          sessions={mySessions}
          autoDeleteDays={myAutoDeleteDays}
          fileAutoDeleteDays={myFileAutoDeleteDays}
          onChanged={reloadMine}
          onClose={closeRules}
        />
      )}
      {showingActivity && (
        <CleanupActivityDialog
          rows={activity}
          canManage={canManage}
          cancelRaceLost={cancelRaceLost}
          cancelOutcome={cancelOutcome}
          // The page's own cancel: it says so when the race was lost, and
          // reloads everything — this list included.
          onCancel={cancelBooking}
          onClose={() => setShowingActivity(false)}
        />
      )}
      {editingRoles && org && (
        <RoleChatDefaultsDialog
          tenantId={tenantId}
          roleDefaults={org.roleDefaults}
          members={org.members}
          quotaBytes={quotaBytes}
          onClose={() => setEditingRoles(false)}
          onSaved={() => { setReloadKey((k) => k + 1); onLimitsChanged?.() }}
        />
      )}
    </Section>
    </ListViewMemory.Provider>
    </SpacePendingFiles.Provider>
  )
}
